import { useState } from 'react';
import type { FC } from 'react';

interface LegalPageProps {
  onBack: () => void;
}

const DownloadButton: FC<{ title: string }> = ({ title }) => {
  const handleDownload = () => {
    const content = document.querySelector('.prose');
    if (!content) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html><html><head><title>${title} - Anchor AI Guard</title>
      <style>body{font-family:system-ui,-apple-system,sans-serif;max-width:800px;margin:40px auto;padding:0 20px;color:#1a1a2e;line-height:1.7}
      h1{color:#0891b2;border-bottom:2px solid #0891b2;padding-bottom:12px}h2{color:#1e293b;margin-top:32px}h3{color:#475569}
      ul{padding-left:24px}li{margin-bottom:8px}table{width:100%;border-collapse:collapse;margin:16px 0}
      td,th{border:1px solid #e2e8f0;padding:8px 12px;text-align:left}th{background:#f1f5f9}
      a{color:#0891b2}p{margin-bottom:12px}.text-sm{font-size:0.875rem;color:#64748b}
      @media print{body{margin:20px}}</style></head><body>
      <h1>${title}</h1><p style="color:#64748b;font-size:14px">Anchor AI Guard Pty Ltd — anchoraiguard.com</p>
      ${content.innerHTML || ''}</body></html>`);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return (
    <button
      onClick={handleDownload}
      className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-lg hover:bg-cyan-500/20 transition-colors"
      title={`Download ${title} as PDF`}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      Download / Print
    </button>
  );
};

export const PrivacyPolicy: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>

        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          Privacy Policy
        </h1>

        <DownloadButton title="Privacy Policy" />

        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200">
          <p className="text-sm text-purple-400">Last updated: February 12, 2026</p>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">1. Introduction</h2>
            <p>
              Anchor AI Guard Pty Ltd (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) is
              committed to protecting your privacy. This Privacy Policy explains how we collect,
              use, disclose, and safeguard your information when you use the Anchor AI Guard
              sovereign-grade cybersecurity platform (&ldquo;Platform&rdquo;), which comprises
              Sovereign Intelligence Architecture, and Sovereign Capabilities capabilities
              across 42 sovereign sector businesses. The Platform operates as Anchor (the organism),
              SYMPHONIC Intelligence (the autonomous engine), and the Cortex (the intelligence core)
              — a self-describing, self-auditing, and self-evolving security platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              2. Information We Collect
            </h2>
            <h3 className="text-xl text-pink-400 mt-4 mb-2">Personal Information</h3>
            <ul className="list-disc list-inside space-y-2">
              <li>Name and email address</li>
              <li>Company/organisation information</li>
              <li>Payment information (processed via sovereign bank transfer)</li>
              <li>Authentication data from OAuth providers (GitHub, Google)</li>
            </ul>

            <h3 className="text-xl text-pink-400 mt-4 mb-2">
              Technical &amp; Security Information
            </h3>
            <ul className="list-disc list-inside space-y-2">
              <li>
                Code repositories, infrastructure configurations, and network data you choose to
                analyse
              </li>
              <li>Security analysis results, threat intelligence, and compliance reports</li>
              <li>
                Endpoint telemetry, SIEM event data, and IoT device metadata (where applicable)
              </li>
              <li>Usage analytics and platform interactions</li>
              <li>Log data and device information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              3. How We Use Your Information
            </h2>
            <ul className="list-disc list-inside space-y-2">
              <li>
                To provide and maintain our autonomous, self-evolving cybersecurity platform
                services across all Sovereign Intelligence Architecture
              </li>
              <li>To process transactions and manage subscriptions</li>
              <li>To improve and personalise your experience</li>
              <li>To communicate with you about service updates</li>
              <li>To detect and prevent security threats</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">4. Data Security</h2>
            <p>We implement industry-standard security measures including:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>End-to-end encryption for all data in transit</li>
              <li>AES-256 encryption for data at rest</li>
              <li>SOC 2 Type II compliance</li>
              <li>Regular security audits and penetration testing</li>
              <li>Zero-knowledge architecture for sensitive code analysis</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">5. Data Retention</h2>
            <p>
              We retain your data only as long as necessary to provide our services. You can request
              deletion of your data at any time by contacting support@anchoraiguard.com.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">6. Your Rights</h2>
            <p>Under GDPR and Australian Privacy Act, you have the right to:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data in a portable format</li>
              <li>Object to data processing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">7. Contact Us</h2>
            <p>
              For privacy-related inquiries, contact us at:
              <br />
              <a
                href="mailto:privacy@anchoraiguard.com"
                className="text-cyan-400 hover:text-pink-400"
              >
                privacy@anchoraiguard.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export const TermsOfService: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>

        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          Terms of Service
        </h1>

        <DownloadButton title="Terms of Service" />

        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200">
          <p className="text-sm text-purple-400">Last updated: February 12, 2026</p>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              1. Acceptance of Terms
            </h2>
            <p>
              By accessing or using the Anchor AI Guard platform (&ldquo;Platform&rdquo;), operated
              by Anchor AI Guard Pty Ltd, you agree to be bound by these Terms of Service. If you do
              not agree to these terms, do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              2. Description of Service
            </h2>
            <p>
              Anchor AI Guard is a category-defining, sovereign-grade cybersecurity platform
              comprising Sovereign Intelligence Architecture, and Sovereign Capabilities
              capabilities across 42 sovereign sector businesses. The Platform operates as Anchor
              (the organism), SYMPHONIC Intelligence (the autonomous engine), and the Cortex (the
              intelligence core) — a self-describing, self-auditing, and self-evolving security
              platform. Services include but are not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                AI security (LLM supply chain, AI agent security, deepfake detection, AI runtime
                protection)
              </li>
              <li>
                Network security (SIEM, WAF, DDoS protection, DNS security, firewall management)
              </li>
              <li>
                Cloud &amp; infrastructure security (CASB, container security, cloud posture
                management)
              </li>
              <li>
                Identity &amp; access management (privileged access, zero trust, identity drift
                detection)
              </li>
              <li>Endpoint protection (EDR, IoT security, satellite communications security)</li>
              <li>
                Compliance &amp; governance (privacy impact assessments, patch management, secure
                code review)
              </li>
              <li>
                Threat intelligence, attack surface management, and autonomous security operations
              </li>
              <li>Code scanning, vulnerability detection, and AI-powered auto-remediation</li>
              <li>
                TAZZIE TV Network — sovereign streaming platform with 11 shows, studio tools,
                creator ecosystem, episode engagement (reactions, comments, watch progress), and
                live viewer analytics
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              2A. Sovereign Sector Business Operations
            </h2>
            <div className="bg-slate-900/50 border border-cyan-500/20 rounded-xl p-6 mb-4">
              <p className="text-cyan-300 font-semibold mb-2">ORGANISATIONAL STRUCTURE</p>
              <p className="text-sm">
                The Anchor AI Guard Platform operates as{' '}
                <strong>42 sovereign sector businesses</strong>, each functioning as an autonomous,
                domain-specialised cybersecurity operation. Every sector business operates
                independently with its own dedicated dashboard, AI evolution engine, threat
                intelligence pipeline, compliance framework, and operational workflow — yet all 42
                are interconnected through <strong>SYMPHONIC Intelligence</strong>, the cross-sector
                autonomous orchestration layer that enables real-time intelligence sharing,
                correlated threat detection, and unified sovereign defence across the entire
                ecosystem.
              </p>
              <p className="text-sm mt-2">
                Each sector business is powered by its own dedicated AI Evolution Engine (Sovereign Engines total including the cross-sector SYMPHONIC Engine, medical research engine,
                and autonomous BD engine), enabling autonomous threat adaptation, self-evolving
                detection rules, and sector-specific intelligence that continuously improves without
                manual intervention.
              </p>
            </div>

            <h3 className="text-xl text-pink-400 mt-6 mb-3">Individual Sector Operations</h3>
            <p className="text-sm text-purple-300 mb-4">
              Each sovereign sector business below operates independently as a standalone
              cybersecurity solution for its domain, with dedicated teams, workflows, compliance
              requirements, and AI capabilities. Sectors are listed with their individual scope of
              operations:
            </p>

            <div className="space-y-3">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <h4 className="text-emerald-400 font-semibold">🏥 1. Anchor Medical</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Healthcare cybersecurity, patient data
                  protection, HIPAA/TGA compliance, medical device security, clinical system
                  integrity, mandatory abuse reporting with &lt;5s dispatch, and safeguarding
                  protocols for 6 authorities.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Cross-references threat intelligence
                  from Defence, LEO, and Insurance sectors for coordinated health-sector threat
                  response.
                </p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h4 className="text-blue-400 font-semibold">💰 2. Anchor Finance</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Financial institution cybersecurity,
                  transaction fraud detection, APRA/ASIC/PCI-DSS compliance, algorithmic trading
                  security, anti-money laundering (AML) intelligence, and real-time market threat
                  monitoring.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares fraud patterns with Crypto,
                  Insurance, Casino, and Interpol sectors for cross-sector financial crime
                  prevention.
                </p>
              </div>
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4">
                <h4 className="text-teal-400 font-semibold">🌍 3. Anchor Climate</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Climate research infrastructure
                  protection, environmental monitoring system security, weather station cyber
                  defence, carbon credit platform integrity, and research data sovereignty.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates environmental threat data
                  with Energy, Agriculture, Water Utilities, and Maritime sectors.
                </p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h4 className="text-amber-400 font-semibold">⚖️ 4. Anchor Legal</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Law firm cybersecurity, eDiscovery
                  protection, privilege management, court system security, case management
                  integrity, and legal document chain-of-custody enforcement.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Coordinates compliance intelligence with
                  LEO, Finance, and Insurance sectors for regulatory alignment.
                </p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h4 className="text-red-400 font-semibold">🛡️ 5. Anchor Defence</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Military and government cybersecurity,
                  classified network protection, ITAR/EAR compliance, signals intelligence security,
                  electronic warfare defence, sovereign weapons system cyber integrity, and
                  TS/SCI-grade operations.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares nation-state threat intelligence
                  with Space, Blackflight, Sovereign Surveillance, and LEO sectors under
                  classification controls.
                </p>
              </div>
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-4">
                <h4 className="text-rose-400 font-semibold">☢️ 6. Anchor Energy</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Power grid cybersecurity, SCADA/ICS
                  protection, nuclear facility cyber defence, renewable energy infrastructure
                  security, oil &amp; gas pipeline monitoring, and critical national infrastructure
                  (CNI) protection.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates OT threat data with Mining,
                  Resources, SmartCity, and Climate sectors for energy supply chain defence.
                </p>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <h4 className="text-cyan-400 font-semibold">⚓ 7. Anchor Maritime</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Vessel cybersecurity, port infrastructure
                  protection, AIS/ECDIS system security, cargo tracking integrity, maritime
                  communication encryption, and sea-lane surveillance.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares maritime threat intelligence with
                  Defence, Logistics, Insurance, and Interpol sectors for coordinated maritime
                  security.
                </p>
              </div>
              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                <h4 className="text-indigo-400 font-semibold">🚀 8. Anchor Space</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Satellite cybersecurity, ground station
                  protection, orbital communications encryption, space debris tracking integrity,
                  launch system security, and space situational awareness.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates orbital threat data with
                  Defence, Aviation, and Sovereign Surveillance sectors for space domain awareness.
                </p>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <h4 className="text-green-400 font-semibold">🌾 9. Anchor Agriculture</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Agricultural technology (AgTech)
                  cybersecurity, precision farming system protection, livestock monitoring security,
                  supply chain traceability, rural IoT defence, and biosecurity data integrity.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares biosecurity intelligence with
                  AgroShield, BioAgri, Climate, and Water Utilities sectors for food supply chain
                  defence.
                </p>
              </div>
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <h4 className="text-purple-400 font-semibold">🎓 10. Anchor Education</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Educational institution cybersecurity,
                  student data protection (FERPA/COPPA), research IP security, campus network
                  defence, learning management system integrity, and academic integrity enforcement.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates student threat patterns with
                  Social, HumanKind, and LEO sectors for campus safety intelligence.
                </p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h4 className="text-blue-400 font-semibold">
                  🚔 11. Anchor LEO (Law Enforcement Operations)
                </h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Law enforcement cybersecurity, digital
                  forensics support, intelligence-led policing, evidence chain-of-custody
                  protection, body camera data integrity, warrant management security, and
                  cross-jurisdictional intelligence coordination.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares law enforcement intelligence with
                  Interpol, Defence, Casino, and Sovereign Surveillance sectors under strict access
                  controls.
                </p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <h4 className="text-emerald-400 font-semibold">🦁 12. Anchor Wildlife</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Wildlife conservation technology security,
                  anti-poaching system protection, species tracking integrity, habitat monitoring
                  defence, illegal trade detection, and ranger communication encryption.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates poaching intelligence with
                  Interpol, LEO, Maritime, and Agriculture sectors for wildlife crime prevention.
                </p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <h4 className="text-yellow-400 font-semibold">🎰 13. Anchor Casino</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Casino and gaming cybersecurity, patron
                  data protection, anti-fraud and anti-cheating detection, gaming commission
                  compliance, transaction monitoring, surveillance system integrity, and responsible
                  gambling enforcement.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares fraud patterns with Finance,
                  Crypto, LEO, and Interpol sectors for cross-industry crime detection.
                </p>
              </div>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <h4 className="text-orange-400 font-semibold">🌐 14. Anchor Interpol</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> International law enforcement intelligence
                  coordination, cross-border cybercrime investigation support, INTERPOL Notice
                  system security, fugitive tracking integrity, mutual legal assistance treaty
                  (MLAT) data protection, and global threat intelligence fusion.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Orchestrates intelligence from LEO,
                  Defence, Maritime, Wildlife, and Casino sectors for multinational operations.
                </p>
              </div>
              <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
                <h4 className="text-sky-400 font-semibold">✈️ 15. Anchor Aviation</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Aviation cybersecurity, aircraft system
                  protection, air traffic control security, airport infrastructure defence,
                  passenger data integrity, drone detection, and aerospace supply chain security.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates aviation threat data with
                  Defence, Space, Logistics, and Insurance sectors for airspace security.
                </p>
              </div>
              <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-4">
                <h4 className="text-pink-400 font-semibold">💝 16. Anchor Charity</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Charitable organisation cybersecurity,
                  donor data protection, ACNC compliance, fundraising platform integrity, grant
                  management security, and beneficiary privacy enforcement.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares fraud intelligence with Finance
                  and Insurance sectors to protect charitable donation pipelines.
                </p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h4 className="text-amber-400 font-semibold">₿ 17. Anchor Crypto</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Cryptocurrency exchange security,
                  blockchain monitoring, smart contract auditing, wallet protection, DeFi protocol
                  security, NFT marketplace integrity, and AUSTRAC/FinCEN compliance.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares blockchain threat intelligence
                  with Finance, Casino, Interpol, and LEO sectors for crypto crime prevention.
                </p>
              </div>
              <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg p-4">
                <h4 className="text-fuchsia-400 font-semibold">🎬 18. Anchor Entertainment</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Media &amp; entertainment cybersecurity,
                  content DRM protection, streaming platform security, IP theft prevention, deepfake
                  detection, event ticketing integrity, and production data sovereignty.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates content piracy intelligence
                  with Legal, Social, and Crypto sectors for IP protection.
                </p>
              </div>
              <div className="bg-stone-500/10 border border-stone-500/30 rounded-lg p-4">
                <h4 className="text-stone-400 font-semibold">⛏️ 19. Anchor Mining</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Mining operations cybersecurity, OT/SCADA
                  protection for mining equipment, remote site communication security, geological
                  data integrity, autonomous vehicle security, and mine safety system protection.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares OT threat intelligence with
                  Energy, Resources, and Logistics sectors for resource extraction supply chain
                  defence.
                </p>
              </div>
              <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-4">
                <h4 className="text-lime-400 font-semibold">🏗️ 20. Anchor Resources</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Natural resources cybersecurity, commodity
                  trading platform protection, exploration data sovereignty, refinery SCADA
                  security, environmental compliance monitoring, and sovereign resource inventory
                  integrity.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates resource infrastructure
                  threats with Mining, Energy, Maritime, and Logistics sectors.
                </p>
              </div>
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4">
                <h4 className="text-violet-400 font-semibold">🛒 21. Anchor Retail</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Retail cybersecurity, POS system
                  protection, e-commerce platform security, customer data (PCI-DSS) compliance,
                  supply chain integrity, inventory management defence, and consumer privacy
                  enforcement.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares payment fraud patterns with
                  Finance, Crypto, and Insurance sectors for retail ecosystem defence.
                </p>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <h4 className="text-green-400 font-semibold">🏆 22. Anchor Sports</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Sports organisation cybersecurity, athlete
                  data protection, match integrity &amp; anti-corruption, stadium infrastructure
                  security, broadcast system defence, wagering platform protection, and anti-doping
                  data integrity.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares match-fixing intelligence with
                  Casino, LEO, Interpol, and Entertainment sectors.
                </p>
              </div>
              <div className="bg-slate-500/10 border border-slate-500/30 rounded-lg p-4">
                <h4 className="text-slate-400 font-semibold">🚗 23. Anchor Automotive</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Automotive cybersecurity, connected
                  vehicle protection, autonomous driving system security, V2X communication defence,
                  EV charging infrastructure security, OEM supply chain integrity, and fleet
                  management protection.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates vehicle threat data with
                  SmartCity, Logistics, Insurance, and Energy sectors for transport ecosystem
                  defence.
                </p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h4 className="text-blue-400 font-semibold">🏦 24. Anchor Insurance</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Insurance cybersecurity, actuarial data
                  protection, claims fraud detection, underwriting system security, policyholder
                  data sovereignty, reinsurance platform integrity, and regulatory compliance
                  (APRA/ASIC).
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Receives cross-sector risk intelligence
                  from Finance, Medical, Automotive, Maritime, and Aviation sectors for cyber
                  insurance pricing.
                </p>
              </div>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <h4 className="text-orange-400 font-semibold">📦 25. Anchor Logistics</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Supply chain cybersecurity, fleet tracking
                  protection, warehouse automation security, customs and border system integrity,
                  last-mile delivery defence, and trade document sovereignty.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates supply chain threat data with
                  Maritime, Aviation, Automotive, Retail, and Resources sectors for end-to-end
                  logistics defence.
                </p>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <h4 className="text-cyan-400 font-semibold">🏙️ 26. Anchor SmartCity</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Smart city infrastructure cybersecurity,
                  IoT sensor network protection, traffic management system security, public
                  transport system defence, smart grid integration, citizen data privacy, and
                  municipal service platform integrity.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Orchestrates urban infrastructure
                  defence with Energy, Automotive, Water Utilities, and LEO sectors.
                </p>
              </div>
              <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-4">
                <h4 className="text-pink-400 font-semibold">📱 27. Anchor Social</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Social media platform cybersecurity,
                  content moderation AI security, user data protection, misinformation detection,
                  bot network identification, online safety enforcement, and digital identity
                  verification.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares misinformation and influence
                  operation intelligence with LEO, Defence, Education, and Interpol sectors.
                </p>
              </div>
              <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-4">
                <h4 className="text-lime-400 font-semibold">🛡️ 28. Anchor AgroShield</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Agricultural supply chain cyber-defence,
                  food safety system protection, biosecurity threat detection, cold-chain monitoring
                  integrity, pesticide and herbicide compliance tracking, and farm-to-table
                  chain-of-custody enforcement.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates biosecurity threats with
                  Agriculture, BioAgri, Climate, and Water Utilities sectors.
                </p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <h4 className="text-emerald-400 font-semibold">🧬 29. Anchor BioAgri</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Biotechnology agriculture cybersecurity,
                  genomic data protection, gene-editing research integrity, GMO compliance tracking,
                  bio-containment system security, and proprietary seed technology IP defence.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares bio-threat intelligence with
                  Agriculture, AgroShield, Medical, and Climate sectors.
                </p>
              </div>
              <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
                <h4 className="text-sky-400 font-semibold">💧 30. Anchor Water Utilities</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Water treatment cybersecurity, SCADA/ICS
                  protection for water infrastructure, pipeline monitoring security, water quality
                  sensor integrity, dam control system defence, and public health water safety
                  compliance.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates critical infrastructure
                  threats with Energy, SmartCity, Climate, and Agriculture sectors.
                </p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h4 className="text-red-400 font-semibold">🦅 31. Anchor Blackflight</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Covert operations cybersecurity,
                  classified mission protection, dark operations communication security,
                  counter-intelligence cyber defence, advanced persistent threat (APT) hunting, and
                  zero-day exploitation countermeasures.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares classified threat intelligence
                  with Defence, Sovereign Surveillance, Space, and Interpol sectors under strictest
                  classification controls.
                </p>
              </div>
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4">
                <h4 className="text-violet-400 font-semibold">
                  👁️ 32. Anchor Sovereign Surveillance
                </h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> National surveillance infrastructure
                  cybersecurity, signals intelligence (SIGINT) platform protection, mass data
                  collection system integrity, lawful intercept compliance, biometric database
                  security, and citizen privacy safeguard enforcement.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Coordinates national security
                  intelligence with Defence, Blackflight, LEO, and Interpol sectors.
                </p>
              </div>
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-4">
                <h4 className="text-rose-400 font-semibold">
                  🤝 33. Anchor HumanKind Intelligence
                </h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Humanitarian organisation cybersecurity,
                  refugee data protection, aid distribution integrity, conflict zone communication
                  security, human rights monitoring, anti-trafficking intelligence, and crisis
                  response coordination security.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares humanitarian threat intelligence
                  with Charity, Medical, Education, and Interpol sectors for global crisis response.
                </p>
              </div>
              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                <h4 className="text-indigo-400 font-semibold">
                  🔬 34. Anchor Semiconductor Intelligence
                </h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Semiconductor fabrication cybersecurity,
                  chip design IP protection, foundry SCADA security, supply chain integrity
                  verification, counterfeit component detection, and export control (ITAR/EAR)
                  compliance for advanced chipsets.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates supply chain threat data with
                  Defence, Space, Automotive, and Quantum sectors for sovereign chip sovereignty.
                </p>
              </div>
              <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg p-4">
                <h4 className="text-fuchsia-400 font-semibold">⚛️ 35. Anchor Quantum</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Quantum computing cybersecurity,
                  post-quantum cryptography implementation, quantum key distribution (QKD)
                  management, quantum-resistant algorithm deployment, quantum research data
                  sovereignty, and hybrid classical-quantum system protection.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Provides quantum-safe cryptographic
                  intelligence to Defence, Finance, Space, and Semiconductor sectors for
                  post-quantum readiness.
                </p>
              </div>
              <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-lg p-4">
                <h4 className="text-yellow-500 font-semibold">⛽ 36. Anchor Fuel</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Fuel supply chain cybersecurity, petroleum
                  infrastructure SCADA protection, fuel terminal security, pipeline monitoring
                  integrity, fuel pricing system defence, tanker fleet tracking, and fuel card fraud
                  detection.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates fuel supply threats with
                  Energy, Mining, Logistics, Maritime, and Defence sectors for sovereign fuel supply
                  chain defence.
                </p>
              </div>
              <div className="bg-amber-600/10 border border-amber-600/30 rounded-lg p-4">
                <h4 className="text-amber-500 font-semibold">
                  🎯 37. Anchor Kinetic-SI (Smart Effector Intelligence)
                </h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Sovereign weapons intelligence, smart
                  effector AI licensing, per-unit royalty management, 3-layer ethical firewall
                  validation (Treaty Compliance, Rules of Engagement, Proportionality Assessment),
                  Dud Mode failsafe, partner registry management (NIOA, Thales, Rheinmetall, BAE,
                  Lockheed, RTX), and ITAR/EAR weapons export compliance.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares weapons system cyber integrity
                  intelligence with Defence, Space, Blackflight, and Sovereign Surveillance sectors
                  under Doctrine Level 6 KINETIC_AUTHORITY classification controls.
                </p>
              </div>
              <div className="bg-blue-600/10 border border-blue-600/30 rounded-lg p-4">
                <h4 className="text-blue-500 font-semibold">📡 38. Anchor Telecom</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Telecommunications infrastructure
                  cybersecurity, 5G/6G network protection, SIM-swap fraud prevention, SS7/Diameter
                  protocol security, mobile core network defence, lawful intercept compliance,
                  roaming fraud detection, and spectrum management integrity.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates telecom threat intelligence
                  with Defence, Sovereign Surveillance, SmartCity, and LEO sectors for
                  communications infrastructure defence.
                </p>
              </div>
              <div className="bg-teal-600/10 border border-teal-600/30 rounded-lg p-4">
                <h4 className="text-teal-500 font-semibold">🧪 39. Anchor Biotech</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Biotechnology research cybersecurity,
                  genomic data sovereignty, pharmaceutical R&amp;D IP protection, clinical trial
                  data integrity, bioinformatics platform security, CRISPR research safeguards, and
                  biodefence pathogen database protection.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares bio-research threat intelligence
                  with Medical, BioAgri, Defence, and Pharmaceutical sectors for life sciences
                  defence.
                </p>
              </div>
              <div className="bg-orange-600/10 border border-orange-600/30 rounded-lg p-4">
                <h4 className="text-orange-500 font-semibold">🏗️ 40. Anchor Construction</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Construction site OT cybersecurity, BIM
                  (Building Information Modelling) data protection, heavy equipment fleet security,
                  project management platform integrity, contractor credentialing verification, and
                  smart building commissioning defence.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates construction infrastructure
                  threats with SmartCity, Energy, Resources, and Insurance sectors for
                  built-environment defence.
                </p>
              </div>
              <div className="bg-purple-600/10 border border-purple-600/30 rounded-lg p-4">
                <h4 className="text-purple-500 font-semibold">🏨 41. Anchor Hospitality</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Hotel and tourism cybersecurity, guest
                  data (PCI/GDPR) protection, property management system security, booking platform
                  integrity, loyalty program fraud prevention, smart-room IoT defence, and venue
                  access control.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Shares hospitality threat patterns with
                  Retail, Entertainment, Finance, and Insurance sectors for tourism ecosystem
                  defence.
                </p>
              </div>
              <div className="bg-rose-600/10 border border-rose-600/30 rounded-lg p-4">
                <h4 className="text-rose-500 font-semibold">💊 42. Anchor Pharmaceutical</h4>
                <p className="text-sm mt-1">
                  <strong>Individual operations:</strong> Pharmaceutical manufacturing
                  cybersecurity, drug development IP protection, supply chain anti-counterfeiting,
                  cold-chain logistics security, clinical trial data sovereignty, TGA/FDA regulatory
                  compliance, and adverse event reporting integrity.
                </p>
                <p className="text-sm mt-1 text-purple-400">
                  <strong>Connected via SYMPHONIC:</strong> Correlates pharma threat intelligence
                  with Medical, Biotech, Logistics, and Insurance sectors for medicines supply chain
                  sovereignty.
                </p>
              </div>
            </div>

            <div className="bg-linear-to-r from-cyan-500/10 to-pink-500/10 border border-cyan-500/30 rounded-xl p-6 mt-6">
              <h4 className="text-white font-semibold mb-2">
                🔗 SYMPHONIC Intelligence — The Connective Layer
              </h4>
              <p className="text-sm">
                While each of the 42 sovereign sector businesses operates as an{' '}
                <strong>autonomous, self-contained cybersecurity operation</strong> with its own
                dedicated engine, dashboard, AI evolution pipeline, and compliance workflow, all
                sectors are <strong>interconnected through SYMPHONIC Intelligence</strong> — the
                cross-sector autonomous orchestration engine. SYMPHONIC Intelligence enables:
              </p>
              <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
                <li>
                  <strong>Cross-Sector Threat Correlation:</strong> A threat detected in one sector
                  is automatically correlated and propagated to all relevant sectors in real-time
                </li>
                <li>
                  <strong>Unified Sovereign Defence:</strong> All 42 businesses contribute to and
                  benefit from a collective intelligence layer that no single-sector solution can
                  replicate
                </li>
                <li>
                  <strong>Autonomous Evolution:</strong> Each sector&apos;s AI engine evolves
                  independently based on its domain-specific threats, while simultaneously learning
                  from cross-sector patterns
                </li>
                <li>
                  <strong>Federation-Grade Operations:</strong> Sectors can form operational
                  coalitions (e.g., Defence + Space + Blackflight for military operations, or
                  Finance + Crypto + Casino for financial crime) without compromising sector-level
                  data sovereignty
                </li>
                <li>
                  <strong>Single Pane of Glass:</strong> Federation Operations Board provides
                  unified command-and-control visibility across all 42 sectors without breaching
                  individual sector access controls
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">3. User Accounts</h2>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials
              and for all activities that occur under your account. You must notify us immediately
              of any unauthorised use.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">4. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Use the service for any illegal purpose</li>
              <li>Attempt to gain unauthorised access to our systems</li>
              <li>Interfere with or disrupt the service</li>
              <li>Reverse engineer or decompile the platform</li>
              <li>Share your account with unauthorised users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">5. Payment Terms</h2>
            <p>
              Subscription fees are billed in advance on a monthly or annual basis. All fees are
              non-refundable except as required by law or as explicitly stated in our refund policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              6. Intellectual Property
            </h2>
            <p>
              The Anchor AI Guard platform, including all software, designs, and content, is owned
              by Anchor AI Guard Pty Ltd. You retain ownership of your code and data; we claim no
              intellectual property rights over the materials you provide.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              7. Limitation of Liability
            </h2>
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
              <p className="text-red-300 font-semibold">IMPORTANT: READ THIS SECTION CAREFULLY</p>
            </div>
            <p>
              To the maximum extent permitted by applicable law (including the Australian Consumer
              Law, Competition and Consumer Act 2010):
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>Aggregate Liability Cap:</strong> Anchor AI Guard&apos;s total aggregate
                liability for all claims arising from or related to these Terms or the Platform
                shall not exceed the fees actually paid by you to Anchor AI Guard in the twelve (12)
                months immediately preceding the event giving rise to the claim.
              </li>
              <li>
                <strong>Exclusion of Consequential Damages:</strong> In no event shall Anchor AI
                Guard, its directors, officers, employees, agents, or affiliates be liable for any
                indirect, incidental, special, consequential, exemplary, or punitive damages,
                including but not limited to: loss of profits, revenue, data, business
                opportunities, goodwill, or anticipated savings; business interruption; cost of
                procurement of substitute services; or any other commercial damages or losses.
              </li>
              <li>
                <strong>Security Breach Limitation:</strong> Anchor AI Guard is not liable for
                security breaches, data loss, unauthorised access, or cyber attacks that occur
                despite use of the Platform. The Platform is designed to assist with security but
                does not guarantee complete protection against all threats.
              </li>
              <li>
                <strong>AI-Generated Recommendations:</strong> Anchor AI Guard is not liable for any
                damages arising from actions taken or not taken based on AI-generated
                recommendations, threat assessments, compliance suggestions, or automated reports.
              </li>
              <li>
                <strong>Third-Party Actions:</strong> Anchor AI Guard is not liable for the actions
                of third parties, including hackers, nation-state actors, or other malicious actors
                who may circumvent security measures.
              </li>
              <li>
                <strong>Regulatory Non-Compliance:</strong> Anchor AI Guard is not liable for
                regulatory fines, penalties, or sanctions imposed on you due to your failure to
                maintain compliance with applicable laws, even if the Platform was used as part of
                your compliance programme.
              </li>
            </ul>
            <p className="mt-4 text-sm text-purple-400">
              Australian Consumer Law: Nothing in these Terms excludes, restricts, or modifies any
              consumer guarantee, right, or remedy conferred by the Australian Consumer Law that
              cannot be excluded, restricted, or modified by agreement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              8. AI and Autonomous Systems Disclaimer
            </h2>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
              <h3 className="text-amber-400 font-semibold">⚠️ CRITICAL: AI SYSTEM LIMITATIONS</h3>
            </div>
            <p>
              The Platform includes autonomous AI systems, including the SYMPHONIC Engine, AI
              Evolution Engine, Autonomous SOC, Autonomous Red Team, AI Auto-Remediation, and Sovereign Autonomous Engines operating across 42 sovereign sector businesses. You acknowledge
              and agree:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>Not Professional Advice:</strong> AI-generated outputs, including threat
                assessments, security recommendations, compliance reports, and remediation
                suggestions, are decision-support tools only and do not constitute professional
                security, legal, financial, medical, or any other form of professional advice.
              </li>
              <li>
                <strong>Human Oversight Required:</strong> Critical security decisions should always
                be reviewed and approved by qualified security professionals. Autonomous operations
                should be monitored and supervised by appropriately trained personnel.
              </li>
              <li>
                <strong>AI Limitations:</strong> AI systems may produce false positives, false
                negatives, hallucinations, or inaccurate outputs. You are solely responsible for
                validating AI-generated content before acting upon it.
              </li>
              <li>
                <strong>No Guarantees:</strong> We do not warrant that AI systems will detect all
                threats, prevent all breaches, or operate without errors. AI capabilities evolve and
                may change without notice.
              </li>
              <li>
                <strong>Autonomous Actions:</strong> Where autonomous remediation or response
                features are enabled, you accept full responsibility for any system changes,
                configurations, or actions taken by the AI, including any unintended consequences.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              9. Sector-Specific Disclaimers
            </h2>
            <p className="text-sm text-purple-300 mb-4">
              Each of the 42 sovereign sector businesses carries sector-specific regulatory,
              ethical, and operational obligations. The following disclaimers apply to your use of
              each sector&apos;s services:
            </p>
            <div className="space-y-4">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <h3 className="text-emerald-400 font-semibold">🏥 Anchor Medical</h3>
                <p className="text-sm mt-2">
                  The Platform and Anchor Medical services are NOT medical devices, NOT
                  FDA-approved, NOT TGA-approved, and do NOT provide clinical advice, diagnosis, or
                  treatment recommendations. They are security and intelligence tools only. All
                  medical decisions must be made by licensed healthcare professionals. We are not
                  liable for patient outcomes, clinical decisions, or healthcare delivery affected
                  by use of our Platform. Mandatory safeguarding and abuse reporting protocols are
                  enforced with &lt;5-second dispatch to 6 relevant authorities.
                </p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-blue-400 font-semibold">💰 Anchor Finance</h3>
                <p className="text-sm mt-2">
                  The Platform and Anchor Finance services do NOT provide investment advice,
                  financial advice, credit advice, or recommendations to buy, sell, or hold any
                  securities, currencies, commodities, or financial instruments. We are not a
                  licensed financial advisor, broker, dealer, or credit provider under ASIC, SEC,
                  FCA, or any other regulator. We are not liable for financial losses, trading
                  decisions, or investment outcomes.
                </p>
              </div>
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4">
                <h3 className="text-teal-400 font-semibold">🌍 Anchor Climate</h3>
                <p className="text-sm mt-2">
                  Anchor Climate provides critical infrastructure protection for climate research
                  and environmental monitoring. It does NOT provide climate predictions, weather
                  forecasts, or environmental impact assessments. We are not liable for
                  environmental decisions, climate policy outcomes, or research conclusions.
                </p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h3 className="text-amber-400 font-semibold">⚖️ Anchor Legal</h3>
                <p className="text-sm mt-2">
                  Anchor Legal services do NOT provide legal advice and do NOT establish an
                  attorney-client relationship. All legal matters should be reviewed by qualified
                  legal professionals in your jurisdiction. We are not liable for legal outcomes,
                  court decisions, regulatory findings, or adverse judgments.
                </p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h3 className="text-red-400 font-semibold">🛡️ Anchor Defence</h3>
                <p className="text-sm mt-2">
                  Anchor Defence services are provided for authorised government and military use
                  only. Users are responsible for ensuring all classified data handling complies
                  with applicable security classifications, clearance requirements, and national
                  security laws. We do not guarantee protection against state-sponsored attacks or
                  advanced persistent threats. Users must comply with ITAR, EAR, and all applicable
                  export control regulations.
                </p>
              </div>
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-4">
                <h3 className="text-rose-400 font-semibold">☢️ Anchor Energy</h3>
                <p className="text-sm mt-2">
                  Anchor Energy provides operational technology (OT) security and critical
                  infrastructure protection. It does NOT replace proper safety systems, physical
                  security measures, or emergency procedures. We are not liable for power outages,
                  grid failures, industrial accidents, environmental incidents, or physical plant
                  damage.
                </p>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <h3 className="text-cyan-400 font-semibold">⚓ Anchor Maritime</h3>
                <p className="text-sm mt-2">
                  Anchor Maritime provides vessel and port cybersecurity. It does NOT replace
                  navigation systems, safety equipment, or maritime operational procedures. We are
                  not liable for collisions, groundings, cargo loss, environmental damage, or
                  maritime incidents.
                </p>
              </div>
              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                <h3 className="text-indigo-400 font-semibold">🚀 Anchor Space</h3>
                <p className="text-sm mt-2">
                  Anchor Space provides satellite and ground station cybersecurity. It does NOT
                  control spacecraft, orbital mechanics, or mission operations. We are not liable
                  for satellite failures, orbital debris incidents, mission failures, or
                  space-related accidents. Compliance with ITU, ITAR, and space agency regulations
                  remains your responsibility.
                </p>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <h3 className="text-green-400 font-semibold">🌾 Anchor Agriculture</h3>
                <p className="text-sm mt-2">
                  Anchor Agriculture provides agricultural technology cybersecurity. It does NOT
                  provide agronomic advice, crop management recommendations, or food safety
                  certifications. We are not liable for crop failures, livestock losses, food safety
                  incidents, or agricultural outcomes.
                </p>
              </div>
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <h3 className="text-purple-400 font-semibold">🎓 Anchor Education</h3>
                <p className="text-sm mt-2">
                  Anchor Education provides educational institution cybersecurity. Student data
                  protection compliance under FERPA, COPPA, and state laws remains your sole
                  responsibility. We are not liable for student data breaches, FERPA violations, or
                  educational outcomes.
                </p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-blue-400 font-semibold">🚔 Anchor LEO</h3>
                <p className="text-sm mt-2">
                  Anchor LEO services are intelligence and operational support tools only. They do
                  NOT replace judicial authorisation, warrants, or proper legal process. Law
                  enforcement users remain solely responsible for ensuring all operations comply
                  with applicable laws, civil rights protections, search and seizure requirements,
                  and chain-of-custody procedures. Intelligence outputs require independent
                  verification and do not constitute evidence. We are not liable for investigations,
                  arrests, prosecutions, or law enforcement actions taken using our services.
                </p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <h3 className="text-emerald-400 font-semibold">🦁 Anchor Wildlife</h3>
                <p className="text-sm mt-2">
                  Anchor Wildlife provides conservation technology security and anti-poaching
                  intelligence. It does NOT replace physical ranger operations, wildlife management
                  decisions, or conservation biology expertise. We are not liable for poaching
                  incidents, species loss, habitat damage, or conservation outcomes.
                </p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <h3 className="text-yellow-400 font-semibold">🎰 Anchor Casino</h3>
                <p className="text-sm mt-2">
                  Anchor Casino provides gaming industry cybersecurity. It does NOT replace gaming
                  commission oversight, responsible gambling programmes, or physical security
                  measures. Users must comply with all applicable gaming regulations and licensing
                  requirements. We are not liable for gambling losses, regulatory actions, or patron
                  incidents.
                </p>
              </div>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <h3 className="text-orange-400 font-semibold">🌐 Anchor Interpol</h3>
                <p className="text-sm mt-2">
                  Anchor Interpol provides international law enforcement intelligence coordination
                  tools. It does NOT replace INTERPOL official systems, sovereign law enforcement
                  authority, or proper diplomatic channels. Cross-border operations must comply with
                  applicable extradition treaties, MLATs, and jurisdictional laws. We are not liable
                  for international investigations, arrests, or diplomatic incidents.
                </p>
              </div>
              <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
                <h3 className="text-sky-400 font-semibold">✈️ Anchor Aviation</h3>
                <p className="text-sm mt-2">
                  Anchor Aviation provides aviation cybersecurity. It does NOT replace certified
                  avionics systems, air traffic control, CASA/FAA certification, or aircraft
                  maintenance procedures. We are not liable for aviation incidents, flight safety
                  events, airworthiness determinations, or passenger safety.
                </p>
              </div>
              <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-4">
                <h3 className="text-pink-400 font-semibold">💝 Anchor Charity</h3>
                <p className="text-sm mt-2">
                  Anchor Charity provides charitable organisation cybersecurity. It does NOT replace
                  ACNC governance requirements, fiduciary obligations, or financial audit processes.
                  We are not liable for donation fraud, charitable trust breaches, or regulatory
                  actions by the ACNC or equivalent bodies.
                </p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h3 className="text-amber-400 font-semibold">₿ Anchor Crypto</h3>
                <p className="text-sm mt-2">
                  Anchor Crypto provides cryptocurrency and blockchain security. It does NOT provide
                  financial advice, trading signals, or token recommendations. Users must comply
                  with AUSTRAC, FinCEN, and applicable digital asset regulations. We are not liable
                  for cryptocurrency losses, smart contract exploits, exchange hacks, or regulatory
                  actions.
                </p>
              </div>
              <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg p-4">
                <h3 className="text-fuchsia-400 font-semibold">🎬 Anchor Entertainment</h3>
                <p className="text-sm mt-2">
                  Anchor Entertainment provides media and entertainment cybersecurity. It does NOT
                  replace content classification systems, broadcast licensing, or copyright
                  enforcement mechanisms. We are not liable for content piracy, IP theft,
                  broadcasting incidents, or entertainment industry disputes.
                </p>
              </div>
              <div className="bg-stone-500/10 border border-stone-500/30 rounded-lg p-4">
                <h3 className="text-stone-400 font-semibold">⛏️ Anchor Mining</h3>
                <p className="text-sm mt-2">
                  Anchor Mining provides mining operations cybersecurity and OT protection. It does
                  NOT replace mine safety systems, geological assessments, or environmental
                  compliance programmes. We are not liable for mining accidents, equipment failures,
                  environmental incidents, or safety violations.
                </p>
              </div>
              <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-4">
                <h3 className="text-lime-400 font-semibold">🏗️ Anchor Resources</h3>
                <p className="text-sm mt-2">
                  Anchor Resources provides natural resources sector cybersecurity. It does NOT
                  replace environmental impact assessments, resource extraction licensing, or
                  commodity trading compliance. We are not liable for resource extraction incidents,
                  commodity losses, or environmental damage.
                </p>
              </div>
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4">
                <h3 className="text-violet-400 font-semibold">🛒 Anchor Retail</h3>
                <p className="text-sm mt-2">
                  Anchor Retail provides retail and e-commerce cybersecurity. It does NOT replace
                  consumer protection obligations, product safety requirements, or PCI-DSS payment
                  compliance. We are not liable for retail fraud, customer data breaches, or
                  regulatory actions by the ACCC or equivalent bodies.
                </p>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <h3 className="text-green-400 font-semibold">🏆 Anchor Sports</h3>
                <p className="text-sm mt-2">
                  Anchor Sports provides sports organisation cybersecurity and match integrity
                  tools. It does NOT replace sports governing body authority, anti-doping testing,
                  or wagering regulation. We are not liable for match-fixing incidents, doping
                  violations, sporting outcomes, or wagering losses.
                </p>
              </div>
              <div className="bg-slate-500/10 border border-slate-500/30 rounded-lg p-4">
                <h3 className="text-slate-400 font-semibold">🚗 Anchor Automotive</h3>
                <p className="text-sm mt-2">
                  Anchor Automotive provides automotive cybersecurity including connected and
                  autonomous vehicle protection. It does NOT replace vehicle safety systems,
                  ANCAP/NHTSA certification, or manufacturer safety recalls. We are not liable for
                  vehicle accidents, autonomous driving incidents, or product liability claims.
                </p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-blue-400 font-semibold">🏦 Anchor Insurance</h3>
                <p className="text-sm mt-2">
                  Anchor Insurance provides insurance industry cybersecurity and risk intelligence.
                  It does NOT provide actuarial advice, underwriting decisions, or insurance policy
                  recommendations. Users must comply with APRA, ASIC, and applicable insurance
                  regulations. We are not liable for claims decisions, underwriting losses, or
                  regulatory actions.
                </p>
              </div>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <h3 className="text-orange-400 font-semibold">📦 Anchor Logistics</h3>
                <p className="text-sm mt-2">
                  Anchor Logistics provides supply chain and logistics cybersecurity. It does NOT
                  replace customs clearance procedures, freight safety regulations, or dangerous
                  goods handling requirements. We are not liable for shipment losses, customs
                  violations, or supply chain disruptions.
                </p>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <h3 className="text-cyan-400 font-semibold">🏙️ Anchor SmartCity</h3>
                <p className="text-sm mt-2">
                  Anchor SmartCity provides smart city infrastructure cybersecurity. It does NOT
                  replace physical municipal services, emergency services, or public safety
                  operations. We are not liable for municipal service disruptions, public safety
                  incidents, or infrastructure failures.
                </p>
              </div>
              <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-4">
                <h3 className="text-pink-400 font-semibold">📱 Anchor Social</h3>
                <p className="text-sm mt-2">
                  Anchor Social provides social media platform cybersecurity and content integrity
                  tools. It does NOT replace content moderation judgement, editorial decisions, or
                  platform governance. Users must comply with the Online Safety Act and applicable
                  social media regulations. We are not liable for content moderation decisions,
                  misinformation incidents, or user safety outcomes.
                </p>
              </div>
              <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-4">
                <h3 className="text-lime-400 font-semibold">🛡️ Anchor AgroShield</h3>
                <p className="text-sm mt-2">
                  Anchor AgroShield provides agricultural supply chain cyber-defence and food safety
                  system protection. It does NOT replace food safety certifications (HACCP/SQF),
                  biosecurity inspections, or agricultural regulatory compliance. We are not liable
                  for food contamination events, biosecurity breaches, or supply chain failures.
                </p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <h3 className="text-emerald-400 font-semibold">🧬 Anchor BioAgri</h3>
                <p className="text-sm mt-2">
                  Anchor BioAgri provides biotechnology agriculture cybersecurity. It does NOT
                  replace OGTR gene technology licensing, biosafety assessments, or scientific peer
                  review. We are not liable for genetic modification outcomes, bio-containment
                  failures, or research integrity disputes.
                </p>
              </div>
              <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
                <h3 className="text-sky-400 font-semibold">💧 Anchor Water Utilities</h3>
                <p className="text-sm mt-2">
                  Anchor Water Utilities provides water infrastructure cybersecurity and SCADA
                  protection. It does NOT replace water quality testing, treatment plant operations,
                  or public health water safety certifications. We are not liable for water
                  contamination events, infrastructure failures, or public health incidents.
                </p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h3 className="text-red-400 font-semibold">🦅 Anchor Blackflight</h3>
                <p className="text-sm mt-2">
                  Anchor Blackflight provides covert operations cybersecurity for authorised
                  government and intelligence agencies only. Users are solely responsible for all
                  classification handling, operational security, and compliance with applicable
                  national security laws. We do not guarantee protection against advanced persistent
                  threats. Unauthorised use is strictly prohibited and may be referred to law
                  enforcement.
                </p>
              </div>
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4">
                <h3 className="text-violet-400 font-semibold">👁️ Anchor Sovereign Surveillance</h3>
                <p className="text-sm mt-2">
                  Anchor Sovereign Surveillance provides national surveillance infrastructure
                  cybersecurity for authorised government agencies only. Users must comply with all
                  applicable surveillance, privacy, and human rights laws including the Surveillance
                  Devices Act 2004, Telecommunications (Interception and Access) Act 1979, and
                  equivalent international legislation. We are not liable for surveillance outcomes,
                  privacy violations, or civil liberties claims.
                </p>
              </div>
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-4">
                <h3 className="text-rose-400 font-semibold">🤝 Anchor HumanKind Intelligence</h3>
                <p className="text-sm mt-2">
                  Anchor HumanKind Intelligence provides humanitarian organisation cybersecurity and
                  crisis response coordination. It does NOT replace humanitarian aid delivery,
                  refugee status determinations, or international humanitarian law obligations. We
                  are not liable for humanitarian outcomes, aid delivery failures, or human rights
                  violations by third parties.
                </p>
              </div>
              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                <h3 className="text-indigo-400 font-semibold">
                  🔬 Anchor Semiconductor Intelligence
                </h3>
                <p className="text-sm mt-2">
                  Anchor Semiconductor provides semiconductor industry cybersecurity and IP
                  protection. It does NOT replace chip fabrication quality assurance, export
                  licensing determinations, or supply chain due diligence. Users must comply with
                  ITAR, EAR, and applicable export controls. We are not liable for fabrication
                  defects, IP theft by third parties, or supply chain integrity failures.
                </p>
              </div>
              <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg p-4">
                <h3 className="text-fuchsia-400 font-semibold">⚛️ Anchor Quantum</h3>
                <p className="text-sm mt-2">
                  Anchor Quantum provides quantum computing cybersecurity and post-quantum
                  cryptography services. It does NOT guarantee quantum-safe protection against all
                  future quantum computing threats. Quantum computing capabilities are evolving
                  rapidly and no current solution can guarantee protection against theoretical
                  future quantum attacks. We are not liable for cryptographic failures, quantum
                  computing breakthroughs, or data compromised by future quantum capabilities.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Anchor AI Guard Pty Ltd, its
              directors, officers, employees, agents, contractors, and affiliates from and against
              any and all claims, damages, losses, liabilities, costs, and expenses (including
              reasonable legal fees) arising from or related to:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                Your use or misuse of the Platform in violation of these Terms or applicable law
              </li>
              <li>Scanning, testing, or analysing systems without proper authorisation</li>
              <li>
                Actions taken based on AI-generated recommendations or autonomous system outputs
              </li>
              <li>Your violation of the Acceptable Use Policy or any other policies</li>
              <li>
                Your failure to maintain adequate security measures beyond use of the Platform
              </li>
              <li>Claims by third parties arising from your use of the Platform</li>
              <li>
                Regulatory investigations, fines, or penalties related to your compliance
                obligations
              </li>
              <li>Security breaches affecting your systems or data</li>
              <li>
                Your violation of any third party&apos;s intellectual property or other rights
              </li>
              <li>
                Use of law enforcement, defence, medical, financial, or other sector-specific
                features in violation of applicable regulations
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">11. Force Majeure</h2>
            <p>
              Anchor AI Guard is not liable for any failure or delay in performance due to causes
              beyond our reasonable control, including but not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                Acts of God, natural disasters, earthquakes, floods, fires, or extreme weather
                events
              </li>
              <li>War, terrorism, civil unrest, riots, or armed conflict</li>
              <li>Government actions, embargoes, sanctions, or regulatory changes</li>
              <li>Pandemics, epidemics, or public health emergencies</li>
              <li>Cyberattacks, nation-state attacks, or critical infrastructure failures</li>
              <li>Third-party service provider outages (cloud, DNS, CDN, payment processors)</li>
              <li>Internet disruptions, telecommunications failures, or power outages</li>
              <li>Labour disputes, strikes, or shortages</li>
              <li>Supply chain disruptions affecting hardware or software dependencies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">12. Governing Law</h2>
            <p>
              These terms are governed by the laws of Australia. Any disputes shall be resolved in
              the courts of New South Wales, Australia.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              13. Dispute Resolution
            </h2>
            <p>Any dispute arising from these Terms shall be resolved as follows:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>Informal Resolution:</strong> You agree to first attempt to resolve any
                dispute informally by contacting legal@anchoraiguard.com. We will work in good faith
                to resolve the matter within 30 days.
              </li>
              <li>
                <strong>Mediation:</strong> If informal resolution fails, disputes shall be
                submitted to mediation administered by the Australian Disputes Centre (ADC) under
                its Mediation Rules.
              </li>
              <li>
                <strong>Arbitration:</strong> If mediation fails, disputes shall be resolved by
                binding arbitration in Sydney, NSW, Australia, under the ADC Arbitration Rules. The
                arbitrator&apos;s decision shall be final and binding.
              </li>
              <li>
                <strong>Class Action Waiver:</strong> You waive any right to participate in class
                action lawsuits or class-wide arbitration against Anchor AI Guard.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">14. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid by a court of
              competent jurisdiction, such provision shall be modified to the minimum extent
              necessary to make it enforceable, or if not possible, severed from these Terms. All
              other provisions shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">15. Entire Agreement</h2>
            <p>
              These Terms, together with the Privacy Policy, Acceptable Use Policy, Disclaimer, and
              any applicable Enterprise Agreement or Master Service Agreement, constitute the entire
              agreement between you and Anchor AI Guard regarding the Platform and supersede all
              prior agreements, representations, and understandings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">16. Contact</h2>
            <p>
              For questions about these terms, contact us at:
              <br />
              <a
                href="mailto:legal@anchoraiguard.com"
                className="text-cyan-400 hover:text-pink-400"
              >
                legal@anchoraiguard.com
              </a>
              <br />
              <br />
              <strong>Anchor AI Guard Pty Ltd</strong>
              <br />
              Sydney, NSW, Australia
              <br />
              ABN: (pending registration)
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export const SecurityPage: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>

        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          Security at Anchor
        </h1>

        <DownloadButton title="Security" />

        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200">
          <p className="text-lg">
            As a security company, we hold ourselves to the highest standards. Here&apos;s how we
            protect your data and our platform.
          </p>

          <section className="bg-slate-900/50 border border-cyan-500/20 rounded-xl p-6 mt-8">
            <h2 className="text-2xl font-semibold text-cyan-400 mb-4">
              🏆 Compliance & Certifications
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">SOC 2 Type II</h3>
                <p className="text-sm mt-1">Annual audits by independent third parties</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">ISO 27001</h3>
                <p className="text-sm mt-1">Information security management certified</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">GDPR Compliant</h3>
                <p className="text-sm mt-1">Full EU data protection compliance</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">Australian Privacy Act</h3>
                <p className="text-sm mt-1">APP compliance for Australian data</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">🔐 Data Protection</h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>Encryption in Transit:</strong> TLS 1.3 for all communications
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>Encryption at Rest:</strong> AES-256 for all stored data
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>Zero-Knowledge Scanning:</strong> Your code never leaves your
                  infrastructure for sensitive scans
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>Data Isolation:</strong> Complete tenant isolation in multi-tenant
                  environment
                </span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              🛡️ Infrastructure Security
            </h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>Cloud Hosting:</strong> Enterprise-grade cloud infrastructure with 99.99%
                  SLA
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>DDoS Protection:</strong> Multi-layer protection against volumetric
                  attacks
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>WAF:</strong> Web application firewall with custom rulesets
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>Penetration Testing:</strong> Quarterly third-party penetration tests
                </span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              🔍 Security Practices
            </h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>Bug Bounty Programme:</strong> Active programme for responsible disclosure
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>Security Training:</strong> Regular training for all employees
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>Incident Response:</strong> 24/7 security operations centre
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400">✓</span>
                <span>
                  <strong>Code Reviews:</strong> All code undergoes security review before
                  deployment
                </span>
              </li>
            </ul>
          </section>

          <section className="bg-linear-to-r from-cyan-500/10 to-pink-500/10 border border-cyan-500/30 rounded-xl p-6 mt-8">
            <h2 className="text-xl font-semibold text-white mb-2">Report a Vulnerability</h2>
            <p className="mb-4">Found a security issue? We appreciate responsible disclosure.</p>
            <a
              href="mailto:security@anchoraiguard.com"
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Contact Security Team
            </a>
          </section>
        </div>
      </div>
    </div>
  );
};

export const AboutPage: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>

        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          About Anchor AI Guard
        </h1>

        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200">
          <p className="text-xl leading-relaxed">
            We built the security tools we wished existed. Sovereign Capabilities innovations across 1,200+
            modules and Sovereign Interconnected Engines across 42 sovereign sector businesses that
            fundamentally change how organisations protect their critical assets, infrastructure,
            and data. Anchor operates as a living organism — Anchor (the organism), SYMPHONIC
            Intelligence (the autonomous engine), and the Cortex (the intelligence core) —
            self-describing, self-auditing, and self-evolving to stay ahead of emerging threats.
          </p>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold text-cyan-400 mb-4">Our Mission</h2>
            <p>
              To democratise enterprise-grade security, making it accessible to organisations of all
              sizes. We believe every company deserves the same level of protection that was once
              only available to Fortune 500 companies.
            </p>
          </section>

          <section className="mt-8">
            <h2 className="text-2xl font-semibold text-cyan-400 mb-4">What Makes Us Different</h2>
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-pink-400 mb-2">🤖 AI-First</h3>
                <p className="text-sm">
                  Built from the ground up with AI at the core, not bolted on as an afterthought.
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-pink-400 mb-2">
                  🚀 World-First Features
                </h3>
                <p className="text-sm">
                  Sovereign Capabilities capabilities that don&apos;t exist anywhere else in the market.
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-pink-400 mb-2">🏛️ Government Ready</h3>
                <p className="text-sm">
                  Purpose-built modules for national security and critical infrastructure.
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-pink-400 mb-2">
                  ⚡ Developer Experience
                </h3>
                <p className="text-sm">
                  Security that developers actually want to use, integrated into existing workflows.
                </p>
              </div>
            </div>
          </section>

          <section className="mt-8">
            <h2 className="text-2xl font-semibold text-cyan-400 mb-4">Founded</h2>
            <p>
              Anchor AI Guard was founded in 2026 in Sydney, Australia, with a vision to
              revolutionise how organisations approach cybersecurity.
            </p>
          </section>

          <section className="bg-linear-to-r from-cyan-500/10 to-pink-500/10 border border-cyan-500/30 rounded-xl p-6 mt-8">
            <h2 className="text-xl font-semibold text-white mb-2">Get in Touch</h2>
            <p className="mb-4">Interested in learning more or joining our team?</p>
            <div className="flex gap-4">
              <a
                href="mailto:hello@anchoraiguard.com"
                className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Contact Us
              </a>
              <a
                href="mailto:careers@anchoraiguard.com"
                className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Careers
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export const PurchaseTerms: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>

        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          Purchase Terms & Conditions
        </h1>

        <DownloadButton title="Purchase Terms" />

        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200">
          <p className="text-sm text-purple-400">Last updated: February 12, 2026</p>

          <p className="text-lg">
            These Purchase Terms & Conditions govern your purchase and use of Anchor AI Guard
            subscription plans and services. By completing a purchase, you agree to these terms.
          </p>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              1. Subscription Plans
            </h2>
            <p>Anchor AI Guard offers the following subscription tiers:</p>
            <div className="grid gap-4 mt-4">
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">Free</h3>
                <p className="text-sm mt-1">
                  $0/month - 1 project, 5 scans/month, basic vulnerability scanning
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">Starter</h3>
                <p className="text-sm mt-1">
                  $990/month - 3 projects, 50 scans/month, all vulnerability scanners, AI Security
                  Chat
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">Pro</h3>
                <p className="text-sm mt-1">
                  $4,990/month - 10 projects, world-first features including Predictive CVE
                  Intelligence &amp; AI Auto-Fix
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">Team</h3>
                <p className="text-sm mt-1">
                  $14,990/month - 50 projects, 15 team members, Digital Twin Security, Autonomous
                  SOC, collaboration
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">Business</h3>
                <p className="text-sm mt-1">
                  $29,990/month - All Sovereign Intelligence Architecture, Sovereign Capabilities, SSO/SAML, 99.9%
                  SLA, dedicated CSM
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">Enterprise</h3>
                <p className="text-sm mt-1">
                  Custom pricing ($5M-$10M/year) - On-premise, custom AI models, 99.95% SLA
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">Enterprise+</h3>
                <p className="text-sm mt-1">
                  Custom pricing ($10M-$25M/year) - Multi-region, white-label, 99.99% SLA
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">Government &amp; Defence</h3>
                <p className="text-sm mt-1">
                  Custom pricing ($25M-$75M+/year) - FedRAMP High, air-gapped, TS/SCI, sovereign
                  deployment
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">2. Billing Cycle</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>Monthly Plans:</strong> Billed on the same date each month
              </li>
              <li>
                <strong>Annual Plans:</strong> Billed annually with up to 20% discount
              </li>
              <li>Billing occurs at the start of each billing period</li>
              <li>Charges will appear as &quot;ANCHOR AI GUARD&quot; on your statement</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">3. Payment Methods</h2>
            <p>We accept payments via direct bank transfer to our sovereign billing account:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Direct bank transfer (all plans)</li>
              <li>International wire transfer (SWIFT: CTBAAU2S)</li>
              <li>Invoice-based billing for Enterprise and Government plans</li>
            </ul>
            <p className="mt-4 text-sm text-purple-400">
              All payments are processed via sovereign bank transfer. Anchor AI Guard does not use
              third-party payment processors or store payment card details.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">4. Demos and access</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                We may offer guided product demonstrations or sandbox walkthroughs so you can
                evaluate the platform
              </li>
              <li>
                Production access for business and enterprise plans requires a paid subscription —
                we do not rely on automatic multi-week free trials
              </li>
              <li>
                Student programmes (where offered) are subject to verification; free tiers are
                described on the relevant product pages
              </li>
              <li>
                Specific promotional credits or discounts are stated at checkout and may change over
                time
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">5. Refund Policy</h2>
            <div className="bg-slate-900/50 border border-green-500/30 rounded-xl p-6 mt-4">
              <h3 className="font-semibold text-green-400 mb-2">30-Day Money-Back Guarantee</h3>
              <p className="text-sm">
                If you&apos;re not satisfied with Anchor AI Guard within the first 30 days of your
                initial subscription, contact us for a full refund. No questions asked.
              </p>
            </div>
            <div className="mt-4 space-y-2">
              <p>
                <strong>After 30 days:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Monthly subscriptions: No refunds for partial months</li>
                <li>Annual subscriptions: Pro-rated refund available within first 90 days</li>
                <li>Refunds are processed within 5-10 business days</li>
                <li>Refunds are returned to the original payment method</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">6. Cancellation</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Cancel anytime from your account settings or by contacting support</li>
              <li>Cancellation takes effect at the end of your current billing period</li>
              <li>You retain access until the end of your paid period</li>
              <li>Data is retained for 30 days after cancellation, then permanently deleted</li>
              <li>You can export your data before cancellation</li>
            </ul>
            <p className="mt-4 text-sm text-purple-400">
              To cancel, go to Settings → Billing → Cancel Subscription, or email{' '}
              <a
                href="mailto:billing@anchoraiguard.com"
                className="text-cyan-400 hover:text-pink-400"
              >
                billing@anchoraiguard.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">7. Plan Changes</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>Upgrades:</strong> Take effect immediately; pro-rated charges apply
              </li>
              <li>
                <strong>Downgrades:</strong> Take effect at next billing cycle
              </li>
              <li>Feature access adjusts according to the new plan</li>
              <li>No penalty fees for changing plans</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">8. Pricing Changes</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>We may update pricing with 30 days advance notice</li>
              <li>Existing subscribers are protected for their current billing cycle</li>
              <li>Annual subscribers keep their rate until renewal</li>
              <li>Price decreases take effect immediately</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">9. Taxes</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Prices displayed exclude applicable taxes unless stated</li>
              <li>GST (10%) applies to Australian customers</li>
              <li>VAT applies to EU customers based on their location</li>
              <li>Sales tax may apply to US customers based on state</li>
              <li>Tax invoices available in your billing dashboard</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              10. Enterprise Agreements
            </h2>
            <p>
              Enterprise and Government customers may have custom terms outlined in a separate
              Master Service Agreement (MSA). In case of conflict, the MSA takes precedence over
              these general purchase terms.
            </p>
            <p className="mt-4">Enterprise agreements may include:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Custom payment terms (NET 30, NET 60)</li>
              <li>Volume discounts</li>
              <li>Custom SLAs</li>
              <li>Dedicated support channels</li>
              <li>On-premise deployment options</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">11. Failed Payments</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>We will retry failed payments up to 3 times over 7 days</li>
              <li>You will receive email notifications of payment failures</li>
              <li>Access may be suspended after multiple failed payment attempts</li>
              <li>Update your payment method in Settings → Billing</li>
              <li>Contact support if you need temporary payment extension</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">12. Disputes</h2>
            <p>If you believe there&apos;s an error with your billing:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Contact us within 60 days of the charge</li>
              <li>Provide transaction details and reason for dispute</li>
              <li>We will investigate and respond within 10 business days</li>
              <li>Valid disputes will be credited or refunded promptly</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              13. AI Media Generation Terms
            </h2>
            <p>
              TAZZIEDEVIL2.0 includes AI-powered media generation capabilities. By using these
              features, you agree to the following terms:
            </p>
            <div className="space-y-4 mt-4">
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">13.1 Available Media Services</h3>
                <ul className="list-disc list-inside space-y-1 text-sm mt-2">
                  <li>
                    <strong>Logo Generation:</strong> AI-generated SVG vector logos with multiple
                    styles and shapes (Sentinel tier+)
                  </li>
                  <li>
                    <strong>PNG Export:</strong> Canvas-rendered PNG export of generated logos
                    (Sovereign tier+)
                  </li>
                  <li>
                    <strong>Text-to-Video:</strong> AI-generated animated video content from text
                    prompts (Sentinel tier+)
                  </li>
                  <li>
                    <strong>Image-to-Video:</strong> Static image animation and video conversion
                    (Sovereign tier+)
                  </li>
                  <li>
                    <strong>Auto-Animate:</strong> Code animation in 50 programming languages
                    (Sentinel tier+)
                  </li>
                  <li>
                    <strong>Audio Generation:</strong> AI-generated audio content including
                    voiceover (Sovereign tier+)
                  </li>
                  <li>
                    <strong>Caption Generation:</strong> AI-powered subtitle and caption creation
                    (Sovereign tier+)
                  </li>
                  <li>
                    <strong>Code Generation:</strong> Syntax-highlighted code in 50 languages
                    (Sentinel tier+)
                  </li>
                </ul>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">13.2 Ownership &amp; Licensing</h3>
                <ul className="list-disc list-inside space-y-1 text-sm mt-2">
                  <li>
                    AI-generated media output is licensed to you for commercial and personal use
                    under your active subscription tier
                  </li>
                  <li>
                    You may not claim AI-generated content as human-created where disclosure is
                    legally required
                  </li>
                  <li>
                    Anchor AI Guard retains no ownership of output generated from your prompts
                  </li>
                  <li>
                    You are responsible for ensuring generated content does not infringe third-party
                    IP
                  </li>
                </ul>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-pink-400">13.3 Fair Use Limits</h3>
                <ul className="list-disc list-inside space-y-1 text-sm mt-2">
                  <li>Media generation is subject to fair use rate limits per tier</li>
                  <li>
                    Excessive automated generation (bot abuse) may result in throttling or
                    suspension
                  </li>
                  <li>
                    Generated media may not be used to create deepfakes, misinformation, or illegal
                    content
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              14. TAZZIEDEVIL2.0 Launch Promotion Terms
            </h2>
            <p>
              The following limited-time, limited-quantity launch promotions apply to the
              TAZZIEDEVIL2.0 and DREADCORE SI product lines. All promotions are first-come,
              first-served and subject to the conditions below.
            </p>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 mt-4">
              <h3 className="font-semibold text-amber-400 mb-2">
                14.1 Student Sovereign Launch — First 2,000,000 Verified Students
              </h3>
              <ul className="list-disc list-inside space-y-2 text-sm mt-2">
                <li>
                  <strong>Offer:</strong> Full TAZZIEDEVIL2.0 Sovereign access (valued at
                  $24.99/month) provided at no cost for 12 consecutive months from the date of
                  activation.
                </li>
                <li>
                  <strong>Eligibility:</strong> New users only. Must verify student status via a
                  valid .edu email address, .ac institutional email, or verified GitHub Student
                  Developer Pack. One activation per individual.
                </li>
                <li>
                  <strong>Verification:</strong> Anchor AI Guard reserves the right to verify
                  student status at any time during the promotional period. Fraudulent verification
                  will result in immediate termination and account suspension.
                </li>
                <li>
                  <strong>Quantity:</strong> Strictly limited to the first 2,000,000 (two million)
                  verified student activations globally. Spots are allocated in chronological order
                  of successful verification.
                </li>
                <li>
                  <strong>Post-Promotion:</strong> After the 12-month promotional period expires,
                  the subscription automatically reverts to the Citizen tier ($0/month) unless the
                  user manually upgrades to a paid plan.
                </li>
                <li>
                  <strong>Value:</strong> Total promotional value per user: $299.88 AUD. This
                  promotional value is non-transferable, non-refundable, and cannot be exchanged for
                  cash or credit.
                </li>
                <li>
                  <strong>Combination:</strong> This promotion may be combined with referral rewards
                  but not with other launch promotions.
                </li>
              </ul>
            </div>

            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-6 mt-4">
              <h3 className="font-semibold text-cyan-400 mb-2">
                14.2 Early Adopter Sovereign — First 1,000,000 Users at 50% Off
              </h3>
              <ul className="list-disc list-inside space-y-2 text-sm mt-2">
                <li>
                  <strong>Offer:</strong> TAZZIEDEVIL2.0 Sovereign subscription at 50% discount
                  ($12.50/month instead of $24.99/month) for the first 3 billing cycles.
                </li>
                <li>
                  <strong>Eligibility:</strong> New, non-student users only. One activation per
                  individual or organisation.
                </li>
                <li>
                  <strong>Quantity:</strong> Strictly limited to the first 1,000,000 (one million)
                  qualifying activations globally.
                </li>
                <li>
                  <strong>Post-Promotion:</strong> After the 3-month discounted period, the
                  subscription automatically reverts to the standard Sovereign tier pricing of
                  $24.99/month. Users will be notified 14 days before the rate change.
                </li>
                <li>
                  <strong>Cancellation:</strong> Users may cancel at any time during or after the
                  promotional period. No refunds are issued for partial months.
                </li>
                <li>
                  <strong>Upgrade:</strong> Users may upgrade to Commander ($49.99/month) or
                  Institutional (custom) tiers at any time. Upgrades are billed at standard rates
                  from the date of upgrade.
                </li>
              </ul>
            </div>

            <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-6 mt-4">
              <h3 className="font-semibold text-pink-400 mb-2">
                14.3 Full TAZZIEDEVIL2.0 Experience — First 500,000 Users at 50% Off
              </h3>
              <ul className="list-disc list-inside space-y-2 text-sm mt-2">
                <li>
                  <strong>Offer:</strong> The complete TAZZIEDEVIL2.0 all-access experience
                  (equivalent to Commander tier and above) at 50% discount for the lifetime of the
                  promotional subscription.
                </li>
                <li>
                  <strong>Scope:</strong> Includes all features across all 42 sovereign sectors,
                  unlimited API access, unlimited team seats, SSO, dedicated infrastructure,
                  white-glove support, and custom integrations.
                </li>
                <li>
                  <strong>Eligibility:</strong> New users only. One activation per individual or
                  organisation.
                </li>
                <li>
                  <strong>Quantity:</strong> Strictly limited to the first 500,000 (five hundred
                  thousand) qualifying activations globally.
                </li>
                <li>
                  <strong>Lifetime Discount:</strong> The 50% discount applies for the continuous
                  lifetime of the subscription. If the subscription lapses (cancelled, failed
                  payment), the promotional pricing is permanently forfeited and re-subscription is
                  at standard rates.
                </li>
                <li>
                  <strong>Enterprise Tiers:</strong> Custom Institutional pricing is not subject to
                  this promotion. Institutional customers should contact sales@anchoraiguard.com for
                  bespoke pricing.
                </li>
              </ul>
            </div>

            <div className="bg-slate-900/50 border border-white/20 rounded-xl p-4 mt-4">
              <p className="text-sm text-purple-300">
                <strong>General Promotion Conditions:</strong> All launch promotions are subject to
                availability and may be withdrawn at Anchor AI Guard&apos;s sole discretion at any
                time without notice once allocation limits are reached. Promotional terms do not
                modify or supersede the core Purchase Terms above. Australian Consumer Law rights
                are not affected. Prices displayed are in AUD unless otherwise stated. GST applies
                to Australian customers. Anchor AI Guard reserves the right to verify the
                eligibility of any promotional claim.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              14. Referral Program Terms
            </h2>
            <p>
              The TAZZIEDEVIL2.0 Referral Program (&ldquo;Referral Program&rdquo;) rewards users who
              refer new users to the platform.
            </p>
            <ul className="list-disc list-inside space-y-2 mt-4">
              <li>
                <strong>Referral Code:</strong> Each registered user receives a unique referral code
                upon account creation.
              </li>
              <li>
                <strong>Reward Structure:</strong>
                <ul className="list-disc list-inside ml-6 space-y-1 mt-1">
                  <li>1 referral: 1 month free Sovereign access</li>
                  <li>3 referrals: 3 months free Sovereign access</li>
                  <li>5 referrals: 6 months free Sovereign access</li>
                  <li>10 referrals: 1 year free Sovereign access</li>
                  <li>25 referrals: Lifetime free Sovereign access</li>
                  <li>50 referrals: Lifetime free Commander access + custom platform badge</li>
                </ul>
              </li>
              <li>
                <strong>Stacking:</strong> Referral rewards stack with launch promotions. There is
                no limit on the number of referrals a user can make.
              </li>
              <li>
                <strong>Qualification:</strong> A referral is counted when the referred user (a)
                creates an account using the referrer&apos;s unique link or code, and (b) completes
                email verification.
              </li>
              <li>
                <strong>Fraud Prevention:</strong> Self-referrals, fake accounts, bot-generated
                signups, and any form of referral manipulation will result in immediate
                disqualification and may result in account suspension.
              </li>
              <li>
                <strong>Reward Application:</strong> Referral rewards are applied automatically to
                the referrer&apos;s account within 48 hours of the referred user completing
                qualification.
              </li>
              <li>
                <strong>Modification:</strong> Anchor AI Guard reserves the right to modify the
                referral reward structure with 30 days&apos; notice. Rewards already earned are not
                affected by changes.
              </li>
              <li>
                <strong>Tax Obligations:</strong> Referral rewards may be considered taxable
                benefits in certain jurisdictions. Users are responsible for any applicable tax
                obligations.
              </li>
            </ul>
          </section>

          <section className="bg-linear-to-r from-cyan-500/10 to-pink-500/10 border border-cyan-500/30 rounded-xl p-6 mt-8">
            <h2 className="text-xl font-semibold text-white mb-2">Billing Support</h2>
            <p className="mb-4">Questions about your subscription, promotions, or billing?</p>
            <a
              href="mailto:billing@anchoraiguard.com"
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Contact Billing Support
            </a>
          </section>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COOKIE POLICY
// ============================================================================
export const CookiePolicy: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          Cookie Policy
        </h1>

        <DownloadButton title="Cookie Policy" />

        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200">
          <p className="text-sm text-purple-400">Last updated: February 12, 2026</p>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">1. What Are Cookies</h2>
            <p>
              Cookies are small text files stored on your device when you visit our website. They
              help us provide you with a better experience by remembering your preferences and
              understanding how you use our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">2. Cookies We Use</h2>
            <h3 className="text-xl text-pink-400 mt-4 mb-2">Strictly Necessary Cookies</h3>
            <p>Required for the platform to function. These cannot be disabled.</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>anchor_auth_token</strong> — Authentication session token
              </li>
              <li>
                <strong>anchor_csrf</strong> — CSRF protection token
              </li>
              <li>
                <strong>anchor_preferences</strong> — Essential user preferences (theme, language)
              </li>
            </ul>

            <h3 className="text-xl text-pink-400 mt-4 mb-2">Analytics Cookies</h3>
            <p>
              Help us understand how visitors interact with our platform. You may opt out of these.
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>_ga / _gid</strong> — Google Analytics (if enabled)
              </li>
              <li>
                <strong>anchor_analytics</strong> — First-party usage analytics
              </li>
            </ul>

            <h3 className="text-xl text-pink-400 mt-4 mb-2">Functional Cookies</h3>
            <p>Enable enhanced functionality and personalisation.</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>anchor_dashboard_state</strong> — Remembers your dashboard layout
              </li>
              <li>
                <strong>anchor_notification_prefs</strong> — Notification preferences
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              3. Third-Party Cookies
            </h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>Sentry</strong> — Error monitoring (performance cookies)
              </li>
            </ul>
            <p className="mt-2">
              We do <strong>not</strong> use advertising cookies or sell cookie data to third
              parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">4. Managing Cookies</h2>
            <p>
              You can control and delete cookies through your browser settings. Note that disabling
              strictly necessary cookies may prevent the platform from functioning correctly.
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>Chrome:</strong> Settings → Privacy and Security → Cookies
              </li>
              <li>
                <strong>Firefox:</strong> Settings → Privacy & Security → Cookies
              </li>
              <li>
                <strong>Safari:</strong> Preferences → Privacy → Manage Website Data
              </li>
              <li>
                <strong>Edge:</strong> Settings → Cookies and Site Permissions
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">5. Cookie Retention</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Session cookies are deleted when you close your browser</li>
              <li>Authentication tokens expire after 7 days of inactivity</li>
              <li>Analytics cookies are retained for a maximum of 13 months</li>
              <li>Preference cookies are retained for 12 months</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              6. Updates to This Policy
            </h2>
            <p>
              We may update this Cookie Policy from time to time. Changes will be posted on this
              page with an updated revision date. Continued use of our platform constitutes
              acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">7. Contact</h2>
            <p>
              For questions about our use of cookies:{' '}
              <a
                href="mailto:privacy@anchoraiguard.com"
                className="text-cyan-400 hover:text-pink-400"
              >
                privacy@anchoraiguard.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ACCEPTABLE USE POLICY
// ============================================================================
export const AcceptableUsePolicy: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          Acceptable Use Policy
        </h1>

        <DownloadButton title="Acceptable Use Policy" />

        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200">
          <p className="text-sm text-purple-400">Last updated: February 12, 2026</p>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">1. Purpose</h2>
            <p>
              This Acceptable Use Policy (&ldquo;AUP&rdquo;) governs your use of Anchor AI
              Guard&apos;s platform, APIs, and services. By using our services, you agree to comply
              with this policy. Violation may result in suspension or termination of your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">2. Permitted Use</h2>
            <p>You may use Anchor AI Guard to:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                Scan and analyse code, applications, and infrastructure{' '}
                <strong>that you own or have explicit authorisation to test</strong>
              </li>
              <li>Generate security reports for your organisation</li>
              <li>Integrate with your CI/CD pipelines and development workflows</li>
              <li>Access threat intelligence data for defensive security purposes</li>
              <li>Use AI analysis capabilities for security assessments of your own systems</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">3. Prohibited Use</h2>
            <p>
              You must <strong>NOT</strong> use Anchor AI Guard to:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                Scan, attack, or probe systems you do not own or have written authorisation to test
              </li>
              <li>
                Conduct unauthorised penetration testing or vulnerability scanning of third-party
                systems
              </li>
              <li>Distribute malware, viruses, or other malicious code</li>
              <li>
                Engage in any form of &ldquo;hacking back&rdquo; or offensive cyber operations,
                except where the platform provides legal defensive measures on your own systems
              </li>
              <li>Circumvent rate limits, usage quotas, or licensing restrictions</li>
              <li>Share, resell, or sublicense your account or API keys</li>
              <li>
                Use the platform for any activity that violates applicable laws, including the
                Australian Criminal Code Act 1995, the Computer Fraud and Abuse Act (US), or the
                Computer Misuse Act (UK)
              </li>
              <li>Abuse the AI features to generate harmful, misleading, or illegal content</li>
              <li>
                Attempt to extract, reverse-engineer, or replicate Anchor&apos;s proprietary
                algorithms, SYMPHONIC Engine, or detection rules
              </li>
              <li>Use the platform to harass, threaten, or intimidate any individual</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              4. Scanning Authorisation
            </h2>
            <p>Before scanning any system, you must ensure:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                You are the owner of the target system, <strong>OR</strong>
              </li>
              <li>You have explicit, written permission from the system owner</li>
              <li>The scan complies with all applicable laws in your jurisdiction</li>
              <li>You have documented authorisation available for audit purposes</li>
            </ul>
            <p className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300">
              <strong>WARNING:</strong> Scanning systems without authorisation is illegal in most
              jurisdictions and may result in criminal prosecution. Anchor AI Guard is{' '}
              <strong>not liable</strong> for any unauthorised scanning activities performed by
              users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">5. Resource Limits</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>API rate limits are enforced per your subscription tier</li>
              <li>Excessive automated requests may be throttled</li>
              <li>Bulk data extraction is prohibited without prior written approval</li>
              <li>Storage limits apply per your subscription plan</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">6. Enforcement</h2>
            <p>Anchor AI Guard reserves the right to:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Monitor usage for compliance with this policy</li>
              <li>Suspend or terminate accounts violating this policy without prior notice</li>
              <li>Report illegal activities to relevant law enforcement authorities</li>
              <li>Cooperate with law enforcement investigations where required by law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              7. Reporting Violations
            </h2>
            <p>
              If you become aware of any violation of this policy, please report it to:{' '}
              <a
                href="mailto:abuse@anchoraiguard.com"
                className="text-cyan-400 hover:text-pink-400"
              >
                abuse@anchoraiguard.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DATA PROCESSING AGREEMENT (DPA)
// ============================================================================
export const DataProcessingAgreement: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          Data Processing Agreement
        </h1>

        <DownloadButton title="Data Processing Agreement" />

        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200">
          <p className="text-sm text-purple-400">Last updated: February 12, 2026</p>
          <p className="text-lg">
            This Data Processing Agreement (&ldquo;DPA&rdquo;) forms part of the Terms of Service
            between Anchor AI Guard Pty Ltd (ABN pending) (&ldquo;Processor&rdquo;) and you
            (&ldquo;Controller&rdquo;) and governs the processing of personal data.
          </p>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">1. Definitions</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>&ldquo;Personal Data&rdquo;</strong> — any data relating to an identified or
                identifiable natural person
              </li>
              <li>
                <strong>&ldquo;Processing&rdquo;</strong> — any operation performed on personal data
                (collection, storage, use, disclosure, deletion)
              </li>
              <li>
                <strong>&ldquo;Sub-processor&rdquo;</strong> — a third party engaged by the
                Processor to process personal data
              </li>
              <li>
                <strong>&ldquo;Data Subject&rdquo;</strong> — the individual to whom personal data
                relates
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              2. Scope of Processing
            </h2>
            <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4 mt-4">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-slate-700">
                    <td className="py-2 text-pink-400 font-semibold w-1/3">Subject matter</td>
                    <td className="py-2">
                      Provision of the Anchor AI Guard sovereign-grade cybersecurity platform
                      (Sovereign Intelligence Architecture, Sovereign Capabilities capabilities across 42 sovereign sector businesses spanning AI
                      security, network security, cloud security, identity &amp; access management,
                      endpoint protection, compliance &amp; governance, and sovereign weapons
                      intelligence) — operating as Anchor (the organism), SYMPHONIC Intelligence
                      (the autonomous engine), and the Cortex (the intelligence core)
                    </td>
                  </tr>
                  <tr className="border-b border-slate-700">
                    <td className="py-2 text-pink-400 font-semibold">Duration</td>
                    <td className="py-2">For the duration of the service agreement plus 30 days</td>
                  </tr>
                  <tr className="border-b border-slate-700">
                    <td className="py-2 text-pink-400 font-semibold">Nature &amp; purpose</td>
                    <td className="py-2">
                      Security analysis of code, infrastructure, networks, endpoints, cloud
                      environments, AI models, and threat data to identify vulnerabilities, monitor
                      compliance, and automate security operations
                    </td>
                  </tr>
                  <tr className="border-b border-slate-700">
                    <td className="py-2 text-pink-400 font-semibold">Categories of data</td>
                    <td className="py-2">
                      User account data, code repositories, scan results, usage logs, IP addresses
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 text-pink-400 font-semibold">Data subjects</td>
                    <td className="py-2">Customer employees, authorised users</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              3. Processor Obligations
            </h2>
            <p>Anchor AI Guard shall:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Process personal data only on documented instructions from the Controller</li>
              <li>Ensure all personnel processing data are bound by confidentiality obligations</li>
              <li>
                Implement appropriate technical and organisational security measures (see Section 4)
              </li>
              <li>Assist the Controller in responding to data subject access requests</li>
              <li>Delete or return all personal data upon termination of the agreement</li>
              <li>Make available all information necessary to demonstrate compliance</li>
              <li>
                Immediately inform the Controller if an instruction infringes applicable data
                protection laws
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">4. Security Measures</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>AES-256 encryption at rest for all personal data</li>
              <li>TLS 1.3 encryption in transit</li>
              <li>Multi-factor authentication for all administrative access</li>
              <li>Role-based access control (RBAC)</li>
              <li>Regular penetration testing and vulnerability assessments</li>
              <li>Automated intrusion detection and prevention</li>
              <li>Comprehensive audit logging</li>
              <li>Disaster recovery with RPO &lt; 1 hour and RTO &lt; 4 hours</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">5. Sub-processors</h2>
            <p>The following sub-processors are authorised:</p>
            <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4 mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 text-pink-400">Sub-processor</th>
                    <th className="text-left py-2 text-pink-400">Purpose</th>
                    <th className="text-left py-2 text-pink-400">Location</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-700">
                    <td className="py-2">Commonwealth Bank of Australia</td>
                    <td className="py-2">Payment processing (bank transfer)</td>
                    <td className="py-2">Australia</td>
                  </tr>
                  <tr className="border-b border-slate-700">
                    <td className="py-2">Anthropic, PBC</td>
                    <td className="py-2">AI analysis (Claude API)</td>
                    <td className="py-2">USA</td>
                  </tr>
                  <tr className="border-b border-slate-700">
                    <td className="py-2">Resend, Inc.</td>
                    <td className="py-2">Email delivery</td>
                    <td className="py-2">USA</td>
                  </tr>
                  <tr className="border-b border-slate-700">
                    <td className="py-2">Supabase, Inc.</td>
                    <td className="py-2">Database hosting</td>
                    <td className="py-2">USA/EU</td>
                  </tr>
                  <tr>
                    <td className="py-2">Sentry (Functional Software)</td>
                    <td className="py-2">Error monitoring</td>
                    <td className="py-2">USA</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4">
              The Controller will be notified at least 30 days before any new sub-processor is
              engaged, with the right to object.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              6. Data Breach Notification
            </h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                Anchor AI Guard will notify the Controller of any personal data breach without undue
                delay and no later than <strong>72 hours</strong> after becoming aware
              </li>
              <li>
                Notification will include: nature of the breach, categories and approximate number
                of data subjects affected, likely consequences, and measures taken to mitigate
              </li>
              <li>
                Anchor AI Guard will cooperate with the Controller and any supervisory authority in
                investigating the breach
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              7. International Transfers
            </h2>
            <p>
              Where personal data is transferred outside the European Economic Area or Australia,
              Anchor AI Guard ensures appropriate safeguards are in place, including:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Standard Contractual Clauses (SCCs) as approved by the European Commission</li>
              <li>Data Processing Agreements with all sub-processors</li>
              <li>Compliance with the Australian Privacy Principles (APPs)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">8. Audit Rights</h2>
            <p>
              The Controller may audit Anchor AI Guard&apos;s compliance with this DPA, subject to
              reasonable notice and scope. Anchor AI Guard will provide reasonable assistance and
              access to information necessary for such audits.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">9. Governing Law</h2>
            <p>
              This DPA is governed by the laws of New South Wales, Australia. For EU data subjects,
              the provisions of the GDPR shall apply. For UK data subjects, the UK GDPR applies.
            </p>
          </section>

          <section className="bg-linear-to-r from-cyan-500/10 to-pink-500/10 border border-cyan-500/30 rounded-xl p-6 mt-8">
            <h2 className="text-xl font-semibold text-white mb-2">Request a Signed DPA</h2>
            <p className="mb-4">
              Enterprise customers can request a countersigned copy of this DPA.
            </p>
            <a
              href="mailto:legal@anchoraiguard.com?subject=DPA%20Request"
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Request Signed DPA
            </a>
          </section>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DISCLAIMER & WARRANTY LIMITATIONS
// ============================================================================
export const Disclaimer: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          Disclaimer &amp; Warranty Limitations
        </h1>

        <DownloadButton title="Disclaimer & Warranty Limitations" />

        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200">
          <p className="text-sm text-purple-400">Last updated: February 12, 2026</p>

          <section className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
            <h2 className="text-2xl font-semibold text-amber-400 mt-0 mb-4">Important Notice</h2>
            <p className="text-amber-200">
              Anchor AI Guard is a{' '}
              <strong>category-defining, sovereign-grade cybersecurity platform</strong> comprising
              Sovereign Intelligence Architecture, and Sovereign Capabilities
              capabilities across 42 sovereign sector businesses — operating as Anchor (the
              organism), SYMPHONIC Intelligence (the autonomous engine), and the Cortex (the
              intelligence core). It is <strong>not</strong> a guarantee of absolute security. No
              software can detect all vulnerabilities or prevent all breaches. Use of our
              autonomous, self-evolving platform should be part of a comprehensive security
              programme, not a substitute for one.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              1. No Guarantee of Complete Security
            </h2>
            <p>
              While Anchor AI Guard employs advanced AI and detection techniques, we do{' '}
              <strong>not</strong> warrant that:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                All vulnerabilities in your code, infrastructure, or applications will be detected
              </li>
              <li>The platform will be free from errors, interruptions, or defects</li>
              <li>Security scan results are exhaustive or infallible</li>
              <li>Use of the platform will prevent all security breaches or data loss</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              2. AI-Generated Content
            </h2>
            <p>
              Anchor AI Guard uses artificial intelligence (including the SYMPHONIC Engine and
              Claude API) to analyse threats and generate recommendations. AI-generated content:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>May contain inaccuracies or false positives/negatives</li>
              <li>Should be reviewed by qualified security professionals before acting upon</li>
              <li>
                Does not constitute professional security advice, legal advice, or compliance
                certification
              </li>
              <li>Is provided as decision-support tooling, not as a definitive assessment</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              3. Compliance Disclaimer
            </h2>
            <p>
              Compliance monitoring features (SOC 2, ISO 27001, GDPR, HIPAA, etc.) are provided to{' '}
              <strong>assist</strong> with compliance efforts. They do <strong>not</strong>{' '}
              constitute legal certification or guarantee regulatory compliance. You remain solely
              responsible for meeting your regulatory obligations. Consult qualified legal and
              compliance professionals.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              4. Threat Intelligence Disclaimer
            </h2>
            <p>
              Threat intelligence data, including CVE information, IOCs, dark web monitoring, and
              predictive analytics:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Is sourced from third-party feeds and public databases</li>
              <li>May not be complete, current, or accurate at all times</li>
              <li>Should not be the sole basis for security decisions</li>
              <li>Is provided &ldquo;as-is&rdquo; without warranty of accuracy</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              5. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by applicable law (including the Australian Consumer
              Law):
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                Anchor AI Guard&apos;s total liability is limited to the fees paid by you in the 12
                months preceding the claim
              </li>
              <li>
                We are not liable for any indirect, incidental, consequential, special, or punitive
                damages
              </li>
              <li>
                We are not liable for loss of data, loss of profit, business interruption, or
                security breaches that occur despite use of our platform
              </li>
              <li>
                We are not liable for actions taken by users based on AI-generated recommendations
              </li>
            </ul>
            <p className="mt-4 text-sm text-purple-400">
              Nothing in this disclaimer excludes or limits liability that cannot be excluded under
              applicable law, including Australian Consumer Law guarantees.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">6. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Anchor AI Guard Pty Ltd, its
              directors, officers, employees, agents, contractors, and affiliates from and against
              any and all claims, damages, losses, liabilities, costs, and expenses (including
              reasonable legal fees on a full indemnity basis) arising from:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Your use of the platform in violation of applicable laws</li>
              <li>Unauthorised scanning of systems you do not own or have permission to test</li>
              <li>Your failure to implement recommended security measures</li>
              <li>Your violation of the Acceptable Use Policy</li>
              <li>Actions taken or not taken based on AI-generated recommendations</li>
              <li>Any security breaches affecting your systems or data</li>
              <li>Regulatory fines, penalties, or sanctions imposed on your organisation</li>
              <li>Claims by third parties arising from your use of the platform</li>
              <li>Your violation of any third party&apos;s rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              7. Sector-Specific Disclaimers
            </h2>
            <div className="space-y-4">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <h3 className="text-emerald-400 font-semibold">🏥 Anchor Medical</h3>
                <p className="text-sm mt-2">
                  Anchor Medical is a cybersecurity and operational intelligence platform. It is NOT
                  a medical device, NOT FDA-approved, NOT TGA-approved, and does NOT provide
                  clinical advice, diagnosis, treatment recommendations, or medical decision
                  support. All medical decisions must be made by licensed healthcare professionals.
                  We disclaim all liability for patient outcomes, clinical decisions, or healthcare
                  delivery affected by use of our platform. Do NOT rely on Anchor Medical for
                  life-critical decisions.
                </p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-blue-400 font-semibold">💰 Anchor Finance</h3>
                <p className="text-sm mt-2">
                  Anchor Finance is a cybersecurity and threat intelligence platform for financial
                  institutions. It does NOT provide investment advice, financial advice, credit
                  advice, or recommendations to buy, sell, or hold any securities, currencies,
                  commodities, or financial instruments. We are not a licensed financial advisor,
                  broker, dealer, or credit provider. We disclaim all liability for financial
                  losses, trading decisions, investment outcomes, or market movements. ASIC, SEC,
                  FCA, and other regulatory compliance remains your sole responsibility.
                </p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h3 className="text-red-400 font-semibold">🛡️ Anchor Defence</h3>
                <p className="text-sm mt-2">
                  Anchor Defence is designed for authorised government and military customers only.
                  Users are solely responsible for ensuring all operations comply with applicable
                  security classifications, clearance requirements, ITAR, EAR, export control
                  regulations, and national security laws. We do NOT guarantee protection against
                  state-sponsored attacks, advanced persistent threats (APTs), or zero-day exploits.
                  Classified data handling is your sole responsibility. Use by unauthorised parties
                  is strictly prohibited.
                </p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-blue-400 font-semibold">🚔 Anchor LEO</h3>
                <p className="text-sm mt-2">
                  Anchor LEO provides intelligence and operational support tools for authorised law
                  enforcement agencies. It does NOT replace judicial authorisation, warrants,
                  subpoenas, or proper legal process. Law enforcement users remain solely
                  responsible for ensuring all operations comply with applicable laws,
                  constitutional protections, civil rights requirements, search and seizure rules,
                  chain-of-custody procedures, and evidence handling protocols. Intelligence outputs
                  require independent verification and do NOT constitute admissible evidence. We
                  disclaim all liability for investigations, arrests, prosecutions, wrongful
                  detentions, or any law enforcement actions taken using our services.
                </p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h3 className="text-amber-400 font-semibold">⚖️ Anchor Legal</h3>
                <p className="text-sm mt-2">
                  Anchor Legal is a cybersecurity and eDiscovery support platform. It does NOT
                  provide legal advice, establish an attorney-client relationship, or replace
                  qualified legal counsel. All legal matters must be reviewed by licensed legal
                  professionals in your jurisdiction. We disclaim all liability for legal outcomes,
                  court decisions, regulatory findings, or adverse judgments.
                </p>
              </div>
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-4">
                <h3 className="text-rose-400 font-semibold">☢️ Anchor Energy</h3>
                <p className="text-sm mt-2">
                  Anchor Energy provides operational technology (OT) security and critical
                  infrastructure protection. It does NOT replace proper safety systems, physical
                  security measures, or emergency procedures. We are not liable for power outages,
                  grid failures, industrial accidents, environmental incidents, or physical plant
                  damage.
                </p>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <h3 className="text-cyan-400 font-semibold">⚓ Anchor Maritime</h3>
                <p className="text-sm mt-2">
                  Anchor Maritime provides vessel and port cybersecurity. It does NOT replace
                  navigation systems, safety equipment, or maritime operational procedures. We are
                  not liable for collisions, groundings, cargo loss, environmental damage, or
                  maritime incidents.
                </p>
              </div>
              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                <h3 className="text-indigo-400 font-semibold">🚀 Anchor Space</h3>
                <p className="text-sm mt-2">
                  Anchor Space provides satellite and ground station cybersecurity. It does NOT
                  control spacecraft, orbital mechanics, or mission operations. We are not liable
                  for satellite failures, orbital debris incidents, mission failures, or
                  space-related accidents. Compliance with ITU, ITAR, and space agency regulations
                  remains your responsibility.
                </p>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <h3 className="text-green-400 font-semibold">🌾 Anchor Agriculture</h3>
                <p className="text-sm mt-2">
                  Anchor Agriculture provides agricultural technology cybersecurity. It does NOT
                  provide agronomic advice, crop management recommendations, or food safety
                  certifications. We are not liable for crop failures, livestock losses, food safety
                  incidents, or agricultural outcomes.
                </p>
              </div>
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <h3 className="text-purple-400 font-semibold">🎓 Anchor Education</h3>
                <p className="text-sm mt-2">
                  Anchor Education provides educational institution cybersecurity. Student data
                  protection compliance under FERPA, COPPA, and state laws remains your sole
                  responsibility. We are not liable for student data breaches, FERPA violations, or
                  educational outcomes.
                </p>
              </div>
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4">
                <h3 className="text-teal-400 font-semibold">🌍 Anchor Climate</h3>
                <p className="text-sm mt-2">
                  Anchor Climate provides critical infrastructure protection for climate research
                  and environmental monitoring. It does NOT provide climate predictions, weather
                  forecasts, or environmental impact assessments. We are not liable for
                  environmental decisions, climate policy outcomes, or research conclusions.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              8. Autonomous AI Systems Disclaimer
            </h2>
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
              <p className="text-red-300 font-semibold">
                ⚠️ CRITICAL: AUTONOMOUS SYSTEM LIMITATIONS
              </p>
            </div>
            <p>
              The Platform includes autonomous AI systems that operate continuously without human
              intervention. These include:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>
                <strong>SYMPHONIC Sovereign Engine:</strong> Autonomous threat response,
                self-evolution, predictive defence
              </li>
              <li>
                <strong>Autonomous SOC:</strong> 24/7 AI-driven security operations
              </li>
              <li>
                <strong>Autonomous Red Team:</strong> Continuous penetration testing and adversarial
                simulation
              </li>
              <li>
                <strong>AI Auto-Remediation:</strong> Automated vulnerability patching and
                configuration fixes
              </li>
              <li>
                <strong>AI Evolution Engine:</strong> Self-modifying threat detection rules
              </li>
            </ul>
            <p className="mt-4">You acknowledge and accept that:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Autonomous systems may take actions without prior human approval when enabled</li>
              <li>
                AI outputs may contain hallucinations, false positives, false negatives, or errors
              </li>
              <li>Critical decisions should always be reviewed by qualified human professionals</li>
              <li>
                You bear sole responsibility for enabling, configuring, and monitoring autonomous
                features
              </li>
              <li>
                Anchor AI Guard disclaims all liability for autonomous system actions or their
                consequences
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">9. Force Majeure</h2>
            <p>
              Anchor AI Guard is not liable for any failure or delay caused by circumstances beyond
              our reasonable control, including but not limited to: natural disasters, war,
              terrorism, cyberattacks, nation-state attacks, government actions, pandemics, internet
              disruptions, power outages, telecommunications failures, third-party service outages,
              labour disputes, or supply chain disruptions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              10. Changes to This Disclaimer
            </h2>
            <p>
              We may update this Disclaimer at any time. Material changes will be notified via the
              Platform or email. Continued use of the Platform after changes constitutes acceptance
              of the updated Disclaimer.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">11. Governing Law</h2>
            <p>
              This Disclaimer is governed by the laws of New South Wales, Australia. Any disputes
              shall be resolved in the courts of New South Wales. For international users, local
              consumer protection laws may apply where they cannot be excluded by contract.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">12. Severability</h2>
            <p>
              If any provision of this Disclaimer is found to be unenforceable, it shall be modified
              to the minimum extent necessary or severed, with all other provisions remaining in
              full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">13. Contact</h2>
            <p>For questions about these disclaimers:</p>
            <p className="mt-2">
              <a
                href="mailto:legal@anchoraiguard.com"
                className="text-cyan-400 hover:text-pink-400"
              >
                legal@anchoraiguard.com
              </a>
              <br />
              <br />
              <strong>Anchor AI Guard Pty Ltd</strong>
              <br />
              Sydney, NSW, Australia
              <br />
              ABN: (pending registration)
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// RESPONSIBLE DISCLOSURE POLICY
// ============================================================================
export const ResponsibleDisclosure: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          Responsible Disclosure Policy
        </h1>

        <DownloadButton title="Responsible Disclosure Policy" />

        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200">
          <p className="text-sm text-purple-400">Last updated: February 12, 2026</p>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">1. Our Commitment</h2>
            <p>
              Anchor AI Guard takes the security of our platform seriously. We welcome and
              appreciate the security research community&apos;s efforts to improve our security. If
              you discover a vulnerability, we want to work with you to resolve it quickly and
              responsibly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">2. Scope</h2>
            <p>The following are in scope for responsible disclosure:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>anchoraiguard.com and all subdomains</li>
              <li>Anchor AI Guard API endpoints</li>
              <li>Anchor AI Guard CLI tools</li>
              <li>Anchor AI Guard mobile applications (when available)</li>
            </ul>
            <p className="mt-4">
              <strong>Out of scope:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Third-party services and integrations</li>
              <li>Social engineering attacks against Anchor employees</li>
              <li>Denial of service attacks</li>
              <li>Physical security of Anchor offices</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">3. How to Report</h2>
            <p>
              Send your findings to:{' '}
              <a
                href="mailto:security@anchoraiguard.com"
                className="text-cyan-400 hover:text-pink-400"
              >
                security@anchoraiguard.com
              </a>
            </p>
            <p className="mt-2">Please include:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Description of the vulnerability</li>
              <li>Steps to reproduce</li>
              <li>Potential impact</li>
              <li>Any proof-of-concept code or screenshots</li>
              <li>Your preferred contact method</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">4. Our Promises</h2>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>We will acknowledge your report within 48 hours</li>
              <li>We will provide a timeline for resolution within 5 business days</li>
              <li>We will not take legal action against researchers acting in good faith</li>
              <li>We will credit you (with your permission) when the issue is resolved</li>
              <li>We aim to resolve critical vulnerabilities within 7 days</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">5. Safe Harbour</h2>
            <p>We consider security research conducted in accordance with this policy to be:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Authorised and not a violation of our Terms of Service</li>
              <li>Lawful and not subject to legal action by Anchor AI Guard</li>
              <li>Helpful and conducted in the public interest</li>
            </ul>
            <p className="mt-4">This safe harbour is conditional on researchers:</p>
            <ul className="list-disc list-inside space-y-2 mt-2">
              <li>Making a good faith effort to avoid privacy violations and data damage</li>
              <li>Not accessing or modifying other users&apos; data</li>
              <li>
                Reporting findings promptly and not disclosing publicly before we have had time to
                address them
              </li>
              <li>Not exploiting the vulnerability beyond what is necessary to demonstrate it</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export const ContactPage: FC<LegalPageProps> = ({ onBack }) => {
  const [submitted, setSubmitted] = useState(false);
  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 8000);
  };
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>

        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          Contact Us
        </h1>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl font-semibold text-cyan-400 mb-6">Get in Touch</h2>

            <div className="space-y-6">
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-xl p-6">
                <h3 className="font-semibold text-pink-400 mb-2">📧 General Inquiries</h3>
                <a
                  href="mailto:hello@anchoraiguard.com"
                  className="text-cyan-400 hover:text-pink-400"
                >
                  hello@anchoraiguard.com
                </a>
              </div>

              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-xl p-6">
                <h3 className="font-semibold text-pink-400 mb-2">💼 Sales</h3>
                <a
                  href="mailto:sales@anchoraiguard.com"
                  className="text-cyan-400 hover:text-pink-400"
                >
                  sales@anchoraiguard.com
                </a>
              </div>

              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-xl p-6">
                <h3 className="font-semibold text-pink-400 mb-2">🛠️ Support</h3>
                <a
                  href="mailto:support@anchoraiguard.com"
                  className="text-cyan-400 hover:text-pink-400"
                >
                  support@anchoraiguard.com
                </a>
              </div>

              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-xl p-6">
                <h3 className="font-semibold text-pink-400 mb-2">🔐 Security</h3>
                <a
                  href="mailto:security@anchoraiguard.com"
                  className="text-cyan-400 hover:text-pink-400"
                >
                  security@anchoraiguard.com
                </a>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-cyan-400 mb-6">Send a Message</h2>

            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
                  <svg
                    className="w-8 h-8 text-emerald-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Message Sent</h3>
                <p className="text-slate-400">
                  Thank you for reaching out. Our team will respond within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-purple-300 mb-1">Name</label>
                  <input
                    type="text"
                    className="w-full bg-slate-900 border border-cyan-500/30 rounded-lg px-4 py-2 text-white focus:border-cyan-400 focus:outline-none"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-purple-300 mb-1">Email</label>
                  <input
                    type="email"
                    className="w-full bg-slate-900 border border-cyan-500/30 rounded-lg px-4 py-2 text-white focus:border-cyan-400 focus:outline-none"
                    placeholder="you@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-purple-300 mb-1">Subject</label>
                  <select
                    aria-label="Subject"
                    className="w-full bg-slate-900 border border-cyan-500/30 rounded-lg px-4 py-2 text-white focus:border-cyan-400 focus:outline-none"
                  >
                    <option>General Inquiry</option>
                    <option>Sales Question</option>
                    <option>Technical Support</option>
                    <option>Partnership Opportunity</option>
                    <option>Press/Media</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-purple-300 mb-1">Message</label>
                  <textarea
                    rows={4}
                    className="w-full bg-slate-900 border border-cyan-500/30 rounded-lg px-4 py-2 text-white focus:border-cyan-400 focus:outline-none resize-none"
                    placeholder="How can we help?"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-linear-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600 text-white font-semibold py-3 rounded-lg transition-all"
                >
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// SERVICE LEVEL AGREEMENT (SLA)
// ═══════════════════════════════════════════════════════════════════
export const ServiceLevelAgreement: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="mb-6 text-cyan-400 hover:text-cyan-300 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-cyan-400">Service Level Agreement</h1>
          <DownloadButton title="Service Level Agreement" />
        </div>
        <p className="text-sm text-purple-400 mb-8">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200 leading-relaxed">
          <p>
            This Service Level Agreement (&quot;SLA&quot;) forms part of the agreement between
            Anchor AI Guard Pty Ltd (ABN: pending registration) (&quot;Anchor&quot;, &quot;we&quot;,
            &quot;us&quot;) and the customer (&quot;you&quot;, &quot;Customer&quot;) for the Anchor
            AI Guard platform.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">1. Service Availability</h2>
          <p>
            Anchor commits to the following monthly uptime percentages based on your subscription
            tier:
          </p>
          <table className="w-full text-sm border-collapse mt-4">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 px-3 text-cyan-400">Tier</th>
                <th className="text-left py-2 px-3 text-cyan-400">Monthly Uptime SLA</th>
                <th className="text-left py-2 px-3 text-cyan-400">Max Monthly Downtime</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">Free / Starter</td>
                <td className="py-2 px-3">99.0%</td>
                <td className="py-2 px-3">~7.3 hours</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">Pro</td>
                <td className="py-2 px-3">99.5%</td>
                <td className="py-2 px-3">~3.6 hours</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">Team</td>
                <td className="py-2 px-3">99.9%</td>
                <td className="py-2 px-3">~43 minutes</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">Business</td>
                <td className="py-2 px-3">99.9%</td>
                <td className="py-2 px-3">~43 minutes</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">Enterprise</td>
                <td className="py-2 px-3">99.95%</td>
                <td className="py-2 px-3">~22 minutes</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">Enterprise+</td>
                <td className="py-2 px-3">99.99%</td>
                <td className="py-2 px-3">~4.3 minutes</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">Government &amp; Defence</td>
                <td className="py-2 px-3">99.99%</td>
                <td className="py-2 px-3">~4.3 minutes</td>
              </tr>
            </tbody>
          </table>

          <h2 className="text-xl font-semibold text-white mt-8">2. Measurement & Exclusions</h2>
          <p>
            Uptime is measured as the percentage of total minutes in a calendar month during which
            the core platform services (dashboard, API, scanning engine) are operational. The
            following are excluded from downtime calculations:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Scheduled maintenance windows (notified 48+ hours in advance, max 4 hours/month)
            </li>
            <li>
              Force majeure events (natural disasters, government actions, pandemic-related
              disruptions)
            </li>
            <li>Customer-caused outages (misconfigured integrations, exceeded rate limits)</li>
            <li>Third-party service failures outside Anchor&apos;s reasonable control</li>
            <li>Network issues between the Customer and Anchor&apos;s infrastructure</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">3. Service Credits</h2>
          <p>
            If Anchor fails to meet the SLA for your tier, you are entitled to service credits as
            follows:
          </p>
          <table className="w-full text-sm border-collapse mt-4">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 px-3 text-cyan-400">Uptime Achieved</th>
                <th className="text-left py-2 px-3 text-cyan-400">
                  Service Credit (% of monthly fee)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">&lt; SLA target but ≥ 99.0%</td>
                <td className="py-2 px-3">10%</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">&lt; 99.0% but ≥ 95.0%</td>
                <td className="py-2 px-3">25%</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">&lt; 95.0%</td>
                <td className="py-2 px-3">50%</td>
              </tr>
            </tbody>
          </table>
          <p>
            Service credits are capped at 50% of your monthly subscription fee. Credits must be
            requested within 30 days of the affected month by emailing support@anchoraiguard.com.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">4. Support Response Times</h2>
          <table className="w-full text-sm border-collapse mt-4">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 px-3 text-cyan-400">Priority</th>
                <th className="text-left py-2 px-3 text-cyan-400">Free / Starter</th>
                <th className="text-left py-2 px-3 text-cyan-400">Pro</th>
                <th className="text-left py-2 px-3 text-cyan-400">Team / Business</th>
                <th className="text-left py-2 px-3 text-cyan-400">
                  Enterprise / Enterprise+ / Gov
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">P1 - Critical</td>
                <td className="py-2 px-3">48h</td>
                <td className="py-2 px-3">4h</td>
                <td className="py-2 px-3">1h</td>
                <td className="py-2 px-3">15 min</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">P2 - High</td>
                <td className="py-2 px-3">72h</td>
                <td className="py-2 px-3">8h</td>
                <td className="py-2 px-3">4h</td>
                <td className="py-2 px-3">1h</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">P3 - Medium</td>
                <td className="py-2 px-3">5 days</td>
                <td className="py-2 px-3">24h</td>
                <td className="py-2 px-3">8h</td>
                <td className="py-2 px-3">4h</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3">P4 - Low</td>
                <td className="py-2 px-3">10 days</td>
                <td className="py-2 px-3">72h</td>
                <td className="py-2 px-3">24h</td>
                <td className="py-2 px-3">8h</td>
              </tr>
            </tbody>
          </table>

          <h2 className="text-xl font-semibold text-white mt-8">5. Incident Communication</h2>
          <p>During any service disruption, Anchor will:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Post real-time updates to our status page at status.anchoraiguard.com</li>
            <li>
              Send email notifications to affected customers within 30 minutes of incident detection
            </li>
            <li>Provide a post-incident report (PIR) within 5 business days of resolution</li>
            <li>For Enterprise customers: direct phone notification via dedicated CSM</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">6. Data Durability</h2>
          <p>
            Anchor maintains 99.999% data durability through geographically redundant backups across
            Australian data centres. All customer data is backed up at minimum every 24 hours, with
            point-in-time recovery available for Enterprise tier customers.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">7. Governing Law</h2>
          <p>
            This SLA is governed by the laws of New South Wales, Australia. Any dispute arising
            under this SLA will be subject to the exclusive jurisdiction of the courts of New South
            Wales.
          </p>

          <p className="text-sm text-gray-500 mt-8 border-t border-gray-700 pt-4">
            Anchor AI Guard Pty Ltd · ABN pending registration · Sydney, Australia ·
            support@anchoraiguard.com
          </p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// END USER LICENCE AGREEMENT (EULA)
// ═══════════════════════════════════════════════════════════════════
export const EndUserLicenceAgreement: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="mb-6 text-cyan-400 hover:text-cyan-300 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-cyan-400">End User Licence Agreement</h1>
          <DownloadButton title="End User Licence Agreement" />
        </div>
        <p className="text-sm text-purple-400 mb-8">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200 leading-relaxed">
          <p>
            <strong>IMPORTANT — READ CAREFULLY:</strong> This End User Licence Agreement
            (&quot;EULA&quot;) is a legal agreement between you (either an individual or an entity)
            and Anchor AI Guard Pty Ltd (ABN: pending registration), trading as Anchor AI Guard
            (&quot;Anchor&quot;, &quot;we&quot;, &quot;us&quot;), for the Anchor AI Guard
            cybersecurity platform (&quot;Software&quot;).
          </p>
          <p>
            By accessing or using the Software, you agree to be bound by this EULA. If you do not
            agree, do not use the Software.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">1. Licence Grant</h2>
          <h3 className="text-lg font-medium text-gray-200 mt-4">1.1 Subscription Licence</h3>
          <p>
            Subject to payment of the applicable subscription fees and compliance with this EULA,
            Anchor grants you a non-exclusive, non-transferable, revocable licence to access and use
            the Software as a cloud-hosted service (SaaS) for the duration of your subscription
            period.
          </p>
          <h3 className="text-lg font-medium text-gray-200 mt-4">
            1.2 Source Code Licence (BUSL-1.1)
          </h3>
          <p>
            The underlying source code of the Software is licensed under the Business Source License
            1.1 (BUSL-1.1). This means:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              You may view, fork, and modify the source code for{' '}
              <strong>non-production purposes</strong> (development, testing, evaluation,
              contribution).
            </li>
            <li>
              You may <strong>not</strong> use the source code to operate a competing commercial
              service.
            </li>
            <li>
              Production use of the Software is exclusively available through paid subscription
              plans on the Anchor platform.
            </li>
            <li>
              The source code converts to a permissive open-source licence automatically on the
              Change Date specified in the LICENSE file.
            </li>
          </ul>
          <h3 className="text-lg font-medium text-gray-200 mt-4">
            1.3 Subscription vs Source Licence
          </h3>
          <p>
            Your paid subscription grants you production-use rights that override the BUSL-1.1
            non-production restriction. If your subscription lapses, your production-use rights
            terminate and you must cease production use within 30 days.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">2. Restrictions</h2>
          <p>You shall not:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Sublicence, sell, resell, rent, or lease the Software to any third party</li>
            <li>Use the Software to build a competing cybersecurity product or service</li>
            <li>
              Reverse engineer, decompile, or disassemble any proprietary components (AI models,
              threat intelligence feeds)
            </li>
            <li>Remove or alter any copyright notices, trademarks, or proprietary markings</li>
            <li>
              Use the Software to conduct malicious activities, attack third parties, or violate any
              applicable law
            </li>
            <li>
              Exceed your subscription tier limits (projects, scans, team members, AI queries)
            </li>
            <li>
              Share your account credentials or allow unauthorised users to access the platform
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">3. Intellectual Property</h2>
          <p>
            All intellectual property rights in the Software, including but not limited to code, AI
            models, algorithms, training data, trade secrets, trademarks (&quot;Anchor AI
            Guard&quot;, the Anchor logo), and documentation, are and remain the exclusive property
            of Anchor AI Guard Pty Ltd.
          </p>
          <p>
            You retain ownership of your data processed through the Software. Anchor claims no
            ownership over your security findings, scan results, or configuration data.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">4. Data Processing</h2>
          <p>
            Our processing of your personal data is governed by our Privacy Policy and Data
            Processing Agreement. By using the Software, you consent to data processing as described
            in those documents.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">5. Warranties & Disclaimers</h2>
          <p>
            The Software is provided &quot;as is&quot; and &quot;as available&quot;. To the maximum
            extent permitted by the Australian Consumer Law (Schedule 2 of the Competition and
            Consumer Act 2010), Anchor disclaims all warranties whether express, implied, or
            statutory, including but not limited to merchantability, fitness for a particular
            purpose, and non-infringement.
          </p>
          <p>
            Anchor does not warrant that the Software will detect all vulnerabilities, prevent all
            security breaches, or that its operation will be uninterrupted or error-free. The
            Software is a tool to assist with cybersecurity and does not replace professional
            security assessment.
          </p>
          <p>
            <strong>
              Nothing in this EULA excludes, restricts, or modifies any consumer guarantee under the
              Australian Consumer Law that cannot lawfully be excluded.
            </strong>
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">6. Limitation of Liability</h2>
          <p>To the maximum extent permitted by law:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Anchor&apos;s total aggregate liability under this EULA shall not exceed the fees paid
              by you in the 12 months preceding the claim.
            </li>
            <li>
              Anchor shall not be liable for any indirect, incidental, special, consequential, or
              punitive damages, including but not limited to loss of profits, data, business
              opportunity, or goodwill.
            </li>
            <li>
              Anchor is not liable for damages arising from any security breach that the Software
              failed to detect or prevent.
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">7. Term & Termination</h2>
          <p>
            This EULA is effective from the date you first access the Software and continues until
            terminated. Anchor may terminate this EULA immediately if you breach any term. Upon
            termination, you must cease all use of the Software and delete any local copies.
            Sections 2, 3, 5, 6, and 9 survive termination.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">8. Export Control</h2>
          <p>
            The Software may be subject to Australian export control laws (Defence Trade Controls
            Act 2012) and applicable international regulations. You shall not export, re-export, or
            transfer the Software to any prohibited country, entity, or individual without
            appropriate government authorisation.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">9. Governing Law</h2>
          <p>
            This EULA is governed by the laws of New South Wales, Australia. Any dispute shall be
            subject to the exclusive jurisdiction of the courts of New South Wales. Before
            commencing legal proceedings, both parties agree to attempt resolution through
            good-faith negotiation for a period of 30 days.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">10. Entire Agreement</h2>
          <p>
            This EULA, together with the Terms of Service, Privacy Policy, SLA, and any applicable
            Order Form, constitutes the entire agreement between you and Anchor regarding the
            Software and supersedes all prior agreements and representations.
          </p>

          <p className="text-sm text-gray-500 mt-8 border-t border-gray-700 pt-4">
            Anchor AI Guard Pty Ltd · ABN pending registration · Sydney, Australia ·
            legal@anchoraiguard.com
          </p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// SOVEREIGN DATA RESIDENCY POLICY
// ═══════════════════════════════════════════════════════════════════
export const SovereignDataResidencyPolicy: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="mb-6 text-cyan-400 hover:text-cyan-300 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-cyan-400">Sovereign Data Residency Policy</h1>
          <DownloadButton title="Sovereign Data Residency Policy" />
        </div>
        <p className="text-sm text-purple-400 mb-8">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200 leading-relaxed">
          <p>
            Anchor AI Guard Pty Ltd (&quot;Anchor&quot;) is an Australian sovereign cybersecurity
            company. This policy outlines our commitment to data sovereignty and the residency of
            customer data within Australian borders.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">
            1. Australian Data Sovereignty Commitment
          </h2>
          <p>
            Anchor is committed to maintaining Australian data sovereignty as a foundational
            principle of our platform:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Australian-owned and operated:</strong> Anchor AI Guard Pty Ltd is a 100%
              Australian-owned company, incorporated in New South Wales, Australia.
            </li>
            <li>
              <strong>No foreign ownership or control:</strong> No foreign government, entity, or
              individual holds a controlling interest in Anchor.
            </li>
            <li>
              <strong>Australian-resident key personnel:</strong> All directors, officers, and key
              security personnel are Australian residents.
            </li>
            <li>
              <strong>Subject to Australian law:</strong> Anchor operates exclusively under
              Australian jurisdiction and law.
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">
            2. Data Storage & Processing Locations
          </h2>
          <h3 className="text-lg font-medium text-gray-200 mt-4">
            2.1 Default — Australian Data Centres
          </h3>
          <p>
            All customer data, including security findings, scan results, configuration data, logs,
            and personal data, is stored and processed exclusively in Australian data centres by
            default. Our primary infrastructure providers operate in:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Sydney, NSW (primary region)</li>
            <li>Melbourne, VIC (disaster recovery)</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-200 mt-4">
            2.2 Government & Defence Customers
          </h3>
          <p>For Government and Defence tier customers, Anchor offers:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Dedicated Australian sovereign cloud infrastructure</li>
            <li>Air-gapped deployment options (no internet connectivity)</li>
            <li>On-premise deployment within customer-controlled facilities</li>
            <li>PROTECTED-level classification support (aligned with Australian Government ISM)</li>
            <li>Data processed only by security-cleared Australian personnel</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-200 mt-4">
            2.3 Enterprise Multi-Region (opt-in only)
          </h3>
          <p>
            Enterprise+ tier customers may opt-in to multi-region deployment for performance
            reasons. In such cases, data residency is contractually agreed upon and documented in
            the customer&apos;s Order Form. No data leaves Australia without explicit written
            consent.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">3. Third-Party Sub-Processors</h2>
          <p>Where Anchor engages sub-processors, we ensure:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              All sub-processors processing Australian customer data maintain Australian-region data
              processing
            </li>
            <li>Sub-processors are subject to contractual data residency obligations</li>
            <li>Customers are notified of any sub-processor changes with 30 days&apos; notice</li>
            <li>A current list of sub-processors is available upon request</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">
            4. International Data Transfer Safeguards
          </h2>
          <p>
            In the limited circumstances where data may be processed outside Australia (e.g., AI
            model inference via cloud providers), Anchor ensures:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Compliance with Australian Privacy Principle (APP) 8 — cross-border disclosure
              requirements
            </li>
            <li>Standard Contractual Clauses (SCCs) or binding corporate rules are in place</li>
            <li>Data is encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
            <li>No personally identifiable information (PII) is included in outbound AI queries</li>
            <li>
              Government and Defence tier customers are never subject to international data
              transfers
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">5. Compliance Framework</h2>
          <p>Anchor&apos;s data residency practices align with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Privacy Act 1988 (Cth)</strong> — Australian Privacy Principles (APPs)
            </li>
            <li>
              <strong>Notifiable Data Breaches (NDB) scheme</strong> — Part IIIC of the Privacy Act
            </li>
            <li>
              <strong>Australian Government Information Security Manual (ISM)</strong>
            </li>
            <li>
              <strong>Security of Critical Infrastructure Act 2018 (SOCI Act)</strong>
            </li>
            <li>
              <strong>Hosting Certification Framework (HCF)</strong> — for government workloads
            </li>
            <li>
              <strong>Essential Eight Maturity Model</strong> — ACSC guidelines
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">6. Customer Rights</h2>
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Request written confirmation of where your data is stored and processed</li>
            <li>Receive notification of any change to data storage locations</li>
            <li>Terminate your subscription if data is moved outside agreed regions</li>
            <li>Request and receive all your data in portable format upon account closure</li>
            <li>Request deletion of all your data within 30 days of account closure</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">7. Contact</h2>
          <p>
            For questions about data residency or to request a Data Residency Certificate for your
            account, contact: <strong>privacy@anchoraiguard.com</strong>
          </p>

          <p className="text-sm text-gray-500 mt-8 border-t border-gray-700 pt-4">
            Anchor AI Guard Pty Ltd · ABN pending registration · Sydney, Australia ·
            privacy@anchoraiguard.com
          </p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// WHISTLEBLOWER POLICY
// ═══════════════════════════════════════════════════════════════════
export const WhistleblowerPolicy: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="mb-6 text-cyan-400 hover:text-cyan-300 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-cyan-400">Whistleblower Policy</h1>
          <DownloadButton title="Whistleblower Policy" />
        </div>
        <p className="text-sm text-purple-400 mb-8">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200 leading-relaxed">
          <p>
            Anchor AI Guard Pty Ltd (&quot;Anchor&quot;) is committed to maintaining the highest
            standards of ethical conduct and corporate governance. This Whistleblower Policy is
            established in compliance with Part 9.4AAA of the Corporations Act 2001 (Cth) and the
            Treasury Laws Amendment (Enhancing Whistleblower Protections) Act 2019.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">1. Purpose</h2>
          <p>
            This policy encourages and facilitates the reporting of misconduct, illegal activity, or
            improper conduct within Anchor, while protecting those who report in good faith from
            retaliation or detrimental action.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">2. Who Can Report</h2>
          <p>Eligible whistleblowers under this policy include:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Current and former employees (including contractors, consultants, interns)</li>
            <li>Current and former officers and directors</li>
            <li>Suppliers and their employees</li>
            <li>Associates and relatives of any of the above</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">3. What Can Be Reported</h2>
          <p>Disclosable matters include conduct that is:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Illegal (fraud, theft, corruption, money laundering, bribery)</li>
            <li>In breach of Commonwealth or state legislation</li>
            <li>A danger to the public or the financial system</li>
            <li>
              Misconduct or an improper state of affairs (e.g., data mishandling, privacy
              violations)
            </li>
            <li>Dishonest, unethical, or in breach of Anchor&apos;s Code of Conduct</li>
            <li>A risk to the security of customer data or platform integrity</li>
          </ul>
          <p>
            <strong>Personal work-related grievances</strong> (e.g., interpersonal conflicts,
            performance management) are generally not covered unless they involve a disclosable
            matter above.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">4. How to Report</h2>
          <p>Reports can be made through any of the following channels:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Email:</strong> whistleblower@anchoraiguard.com (monitored by Whistleblower
              Protection Officer)
            </li>
            <li>
              <strong>Mail:</strong> Whistleblower Protection Officer, Anchor AI Guard Pty Ltd,
              Sydney NSW, Australia (marked &quot;Confidential&quot;)
            </li>
            <li>
              <strong>External regulators:</strong> ASIC, APRA, ATO, or the AFP as applicable
            </li>
            <li>
              <strong>Legal practitioner:</strong> for the purpose of obtaining legal advice
            </li>
          </ul>
          <p>
            Reports may be made <strong>anonymously</strong>. Anonymous disclosures are still
            protected under the Corporations Act.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">
            5. Protections for Whistleblowers
          </h2>
          <p>
            Under the Corporations Act 2001 and this policy, eligible whistleblowers are protected
            from:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Termination or demotion</li>
            <li>Harassment, victimisation, or discrimination</li>
            <li>Threats or other detrimental conduct</li>
            <li>Civil, criminal, or administrative liability for making the disclosure</li>
          </ul>
          <p>
            Any person who retaliates against a whistleblower will be subject to disciplinary
            action, up to and including termination, and may face criminal penalties under the
            Corporations Act.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">6. Confidentiality</h2>
          <p>
            The identity of a whistleblower (or information likely to identify them) will be kept
            strictly confidential, subject only to:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Consent of the whistleblower</li>
            <li>Disclosure to ASIC, APRA, the AFP, or a legal practitioner</li>
            <li>Disclosure required by law or court order</li>
          </ul>
          <p>
            Breach of confidentiality is a criminal offence under the Corporations Act and will
            result in disciplinary action.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">7. Investigation Process</h2>
          <p>
            All disclosures will be assessed within 7 days. Where an investigation is warranted,
            Anchor will:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Appoint an independent investigator (internal or external as appropriate)</li>
            <li>Conduct the investigation in a fair, objective, and timely manner</li>
            <li>Keep the whistleblower informed of progress (where identity is known)</li>
            <li>Document findings and implement remedial actions as required</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">
            8. Whistleblower Protection Officer
          </h2>
          <p>
            Anchor&apos;s designated Whistleblower Protection Officer is responsible for overseeing
            the implementation of this policy, ensuring disclosures are handled appropriately, and
            that whistleblowers are protected from detriment.
          </p>
          <p>
            Contact: <strong>whistleblower@anchoraiguard.com</strong>
          </p>

          <p className="text-sm text-gray-500 mt-8 border-t border-gray-700 pt-4">
            Anchor AI Guard Pty Ltd · ABN pending registration · Sydney, Australia · This policy is
            reviewed annually and approved by the Board of Directors.
          </p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// INCIDENT RESPONSE POLICY
// ═══════════════════════════════════════════════════════════════════
export const IncidentResponsePolicy: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="mb-6 text-cyan-400 hover:text-cyan-300 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-cyan-400">Incident Response Policy</h1>
          <DownloadButton title="Incident Response Policy" />
        </div>
        <p className="text-sm text-purple-400 mb-8">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200 leading-relaxed">
          <p>
            Anchor AI Guard Pty Ltd (&quot;Anchor&quot;) maintains a comprehensive incident response
            programme to detect, respond to, and recover from security incidents affecting our
            platform, infrastructure, and customer data.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">1. Scope</h2>
          <p>This policy applies to all security incidents involving:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Unauthorised access to Anchor systems, infrastructure, or customer data</li>
            <li>
              Data breaches (confirmed or suspected loss, theft, or exposure of personal
              information)
            </li>
            <li>Denial-of-service attacks affecting platform availability</li>
            <li>Malware, ransomware, or other malicious code in Anchor infrastructure</li>
            <li>Compromise of employee credentials or internal systems</li>
            <li>Supply chain compromise affecting Anchor dependencies</li>
            <li>Physical security incidents affecting data centre operations</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">2. Incident Classification</h2>
          <table className="w-full text-sm border-collapse mt-4">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 px-3 text-cyan-400">Severity</th>
                <th className="text-left py-2 px-3 text-cyan-400">Description</th>
                <th className="text-left py-2 px-3 text-cyan-400">Response Time</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3 font-medium text-red-400">P1 — Critical</td>
                <td className="py-2 px-3">
                  Active data breach, complete platform outage, active attacker in systems
                </td>
                <td className="py-2 px-3">15 minutes</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3 font-medium text-orange-400">P2 — High</td>
                <td className="py-2 px-3">
                  Suspected breach, partial outage, vulnerability under active exploitation
                </td>
                <td className="py-2 px-3">1 hour</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3 font-medium text-yellow-400">P3 — Medium</td>
                <td className="py-2 px-3">
                  Anomalous activity, vulnerability discovered, failed attack attempts
                </td>
                <td className="py-2 px-3">4 hours</td>
              </tr>
              <tr className="border-b border-gray-800">
                <td className="py-2 px-3 font-medium text-blue-400">P4 — Low</td>
                <td className="py-2 px-3">
                  Informational security event, policy violation, minor misconfiguration
                </td>
                <td className="py-2 px-3">24 hours</td>
              </tr>
            </tbody>
          </table>

          <h2 className="text-xl font-semibold text-white mt-8">3. Response Phases</h2>
          <h3 className="text-lg font-medium text-gray-200 mt-4">3.1 Detection & Triage</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>24/7 automated monitoring via SIEM, IDS/IPS, and AI anomaly detection</li>
            <li>Initial assessment and severity classification within 15 minutes of detection</li>
            <li>Incident Commander assigned from on-call rotation</li>
          </ul>
          <h3 className="text-lg font-medium text-gray-200 mt-4">3.2 Containment</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Immediate isolation of affected systems</li>
            <li>Credential rotation for compromised accounts</li>
            <li>Network segmentation to prevent lateral movement</li>
            <li>Preservation of forensic evidence</li>
          </ul>
          <h3 className="text-lg font-medium text-gray-200 mt-4">3.3 Eradication & Recovery</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Root cause identification and elimination</li>
            <li>System restoration from verified clean backups</li>
            <li>Security patch deployment</li>
            <li>Phased service restoration with enhanced monitoring</li>
          </ul>
          <h3 className="text-lg font-medium text-gray-200 mt-4">3.4 Post-Incident Review</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Post-Incident Report (PIR) published within 5 business days</li>
            <li>Lessons learned incorporated into runbooks and training</li>
            <li>Preventive controls implemented to address root cause</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">
            4. Notifiable Data Breaches (NDB Scheme)
          </h2>
          <p>In compliance with Part IIIC of the Privacy Act 1988 (Cth), Anchor will:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Assess whether a data breach is &quot;eligible&quot; (likely to result in serious
              harm) within 30 days of becoming aware
            </li>
            <li>
              Notify the <strong>Office of the Australian Information Commissioner (OAIC)</strong>{' '}
              as soon as practicable if an eligible data breach is confirmed
            </li>
            <li>Notify all affected individuals whose personal information was involved</li>
            <li>
              Include in notifications: description of the breach, types of information involved,
              steps individuals should take, and Anchor&apos;s contact details
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">5. Customer Notification</h2>
          <p>For incidents affecting customer data or platform availability:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>P1/P2:</strong> Affected customers notified within 1 hour via email and status
              page
            </li>
            <li>
              <strong>P3:</strong> Notification within 24 hours
            </li>
            <li>
              <strong>P4:</strong> Included in monthly security digest
            </li>
            <li>Enterprise customers receive direct phone notification from their CSM</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">6. Contact</h2>
          <p>
            To report a security incident: <strong>security@anchoraiguard.com</strong> (24/7
            monitored)
          </p>
          <p>
            For responsible disclosure of vulnerabilities, see our{' '}
            <strong>Responsible Disclosure Policy</strong>.
          </p>

          <p className="text-sm text-gray-500 mt-8 border-t border-gray-700 pt-4">
            Anchor AI Guard Pty Ltd · ABN pending registration · Sydney, Australia · This policy is
            reviewed quarterly and tested through annual tabletop exercises.
          </p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// ACCESSIBILITY STATEMENT
// ═══════════════════════════════════════════════════════════════════
export const AccessibilityStatement: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="mb-6 text-cyan-400 hover:text-cyan-300 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-cyan-400">Accessibility Statement</h1>
          <DownloadButton title="Accessibility Statement" />
        </div>
        <p className="text-sm text-purple-400 mb-8">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200 leading-relaxed">
          <p>
            Anchor AI Guard Pty Ltd (&quot;Anchor&quot;) is committed to ensuring that the Anchor AI
            Guard platform is accessible to all users, including people with disabilities, in
            accordance with the Disability Discrimination Act 1992 (Cth) and the Web Content
            Accessibility Guidelines (WCAG) 2.1.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">1. Our Commitment</h2>
          <p>
            We strive to ensure that our platform meets WCAG 2.1 Level AA conformance. Our
            accessibility efforts include:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Semantic HTML structure with proper heading hierarchy</li>
            <li>ARIA labels and roles for interactive elements</li>
            <li>Keyboard navigation support throughout the platform</li>
            <li>Sufficient colour contrast ratios (minimum 4.5:1 for normal text)</li>
            <li>Text alternatives for non-text content</li>
            <li>Responsive design that supports zoom up to 200%</li>
            <li>Focus indicators for keyboard users</li>
            <li>Screen reader compatibility testing</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">2. Known Limitations</h2>
          <p>
            While we strive for full compliance, certain areas of the platform may have limitations:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Some data visualisations (charts, attack path graphs) may not be fully accessible to
              screen readers. We provide alternative data tables where possible.
            </li>
            <li>
              Real-time threat feed updates may not be immediately announced to screen readers.
            </li>
            <li>PDF exports may have limited accessibility depending on the PDF reader used.</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">3. Assistive Technologies</h2>
          <p>Anchor AI Guard has been tested with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>NVDA (Windows) and VoiceOver (macOS/iOS)</li>
            <li>JAWS screen reader</li>
            <li>Browser zoom up to 400%</li>
            <li>High contrast mode (Windows)</li>
            <li>Voice control software</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">4. Feedback & Requests</h2>
          <p>
            If you encounter any accessibility barriers or have suggestions for improvement, please
            contact us:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Email: <strong>accessibility@anchoraiguard.com</strong>
            </li>
            <li>We aim to respond to accessibility feedback within 5 business days</li>
            <li>We commit to resolving critical accessibility issues within 30 days</li>
          </ul>

          <h2 className="text-xl font-semibold text-white mt-8">5. Conformance Status</h2>
          <p>
            Current status: <strong>Partially conformant</strong> with WCAG 2.1 Level AA. We are
            actively working toward full conformance and conduct accessibility audits quarterly.
          </p>

          <h2 className="text-xl font-semibold text-white mt-8">6. Legal Framework</h2>
          <p>This statement is made in compliance with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Disability Discrimination Act 1992 (Cth)</strong>
            </li>
            <li>
              <strong>Disability Standards for Accessible Public Transport 2002</strong> (where
              applicable)
            </li>
            <li>
              <strong>WCAG 2.1 Level AA</strong> — Web Content Accessibility Guidelines
            </li>
            <li>
              <strong>EN 301 549</strong> — European accessibility requirements (for international
              customers)
            </li>
          </ul>

          <p className="text-sm text-gray-500 mt-8 border-t border-gray-700 pt-4">
            Anchor AI Guard Pty Ltd · ABN pending registration · Sydney, Australia ·
            accessibility@anchoraiguard.com
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// INTELLECTUAL PROPERTY & PATENT NOTICE
// ============================================================================
export const IntellectualPropertyNotice: FC<LegalPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={onBack}
          className="mb-8 text-cyan-400 hover:text-pink-400 flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>

        <h1 className="text-4xl font-bold mb-8 bg-linear-to-r from-cyan-400 to-pink-400 bg-clip-text text-transparent">
          Intellectual Property &amp; Patent Notice
        </h1>

        <DownloadButton title="Intellectual Property Notice" />

        <div className="prose prose-invert prose-cyan max-w-none space-y-6 text-purple-200">
          <p className="text-sm text-purple-400">Last updated: March 2, 2026</p>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              1. Ownership &amp; Rights Holder
            </h2>
            <p>
              All intellectual property rights in and to the Anchor AI Guard platform, including but
              not limited to all software, source code, algorithms, AI models, architectures,
              designs, trade marks, trade dress, logos, documentation, marketing materials, and
              associated technologies, are the exclusive property of:
            </p>
            <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4 mt-4">
              <p className="text-cyan-300 font-semibold">
                MR TODD ANTHONY MATHEW GOODWIN TA ANCHOR AI GUARD
              </p>
              <p className="text-sm mt-1">Trading as Anchor AI Guard</p>
              <p className="text-sm">Co-founders: Todd Goodwin &amp; Sarah Goodwin</p>
              <p className="text-sm">Jurisdiction: Australia</p>
              <p className="text-sm">Domain: www.anchoraiguard.com</p>
            </div>
            <p className="mt-4">
              &copy; 2024-2026 Anchor AI Guard. All rights reserved worldwide. No part of this
              platform, its code, its intellectual property, its AI models, or its operational
              methodologies may be reproduced, distributed, transmitted, reverse-engineered, or
              otherwise used without the express written consent of the rights holder.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">2. Trade Marks</h2>
            <p>
              The following names, logos, and marks are trade marks (registered or pending
              registration) of Anchor AI Guard:
            </p>

            <h3 className="text-xl text-pink-400 mt-6 mb-3">2.1 Primary Platform Marks</h3>
            <div className="grid gap-3 mt-4">
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h4 className="font-semibold text-pink-400">ANCHOR AI GUARD&trade;</h4>
                <p className="text-sm mt-1">
                  The sovereign-grade cybersecurity platform. Covers: cybersecurity software, AI
                  security platforms, autonomous threat detection systems, compliance management
                  tools.
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h4 className="font-semibold text-pink-400">ANCHOR OS&trade;</h4>
                <p className="text-sm mt-1">
                  The sovereign operating system layer. Covers: operating system software for
                  cybersecurity operations, institutional security deployment platforms, multi-layer
                  security orchestration software.
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h4 className="font-semibold text-pink-400">
                  DREADCORE SI&trade; (SYMPHONIC Intelligence)
                </h4>
                <p className="text-sm mt-1">
                  The autonomous AI intelligence engine. Covers: artificial intelligence software,
                  autonomous security operations systems, cross-sector intelligence correlation
                  engines, self-evolving AI platforms.
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h4 className="font-semibold text-pink-400">TAZZIEDEVIL2.0&trade;</h4>
                <p className="text-sm mt-1">
                  The sovereign AI chatbot and multi-modal intelligence product. Covers: AI chatbot
                  software, multi-modal AI products, sovereign AI assistants, text-to-video AI,
                  audio generation AI, code generation AI.
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h4 className="font-semibold text-pink-400">SYMPHONIC INTELLIGENCE&trade;</h4>
                <p className="text-sm mt-1">
                  The cross-sector autonomous orchestration engine. Covers: AI orchestration
                  software, cross-domain intelligence correlation, autonomous multi-sector
                  cybersecurity coordination.
                </p>
              </div>
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h4 className="font-semibold text-pink-400">THE CORTEX&trade;</h4>
                <p className="text-sm mt-1">
                  The intelligence core of the Anchor platform. Covers: centralised AI
                  decision-making systems, intelligence aggregation platforms, neural security core
                  software.
                </p>
              </div>
            </div>

            <h3 className="text-xl text-pink-400 mt-6 mb-3">2.2 Sector Business Marks</h3>
            <p className="text-sm">
              Each of the following 42 sovereign sector business names is a trade mark of Anchor AI
              Guard:
            </p>
            <div className="grid md:grid-cols-2 gap-2 mt-3">
              {[
                'Anchor Medical',
                'Anchor Finance',
                'Anchor Climate',
                'Anchor Legal',
                'Anchor Defence',
                'Anchor Energy',
                'Anchor Maritime',
                'Anchor Space',
                'Anchor Agriculture',
                'Anchor Education',
                'Anchor LEO',
                'Anchor Wildlife',
                'Anchor Casino',
                'Anchor Interpol',
                'Anchor Aviation',
                'Anchor Charity',
                'Anchor Crypto',
                'Anchor Entertainment',
                'Anchor Mining',
                'Anchor Resources',
                'Anchor Retail',
                'Anchor Sports',
                'Anchor Automotive',
                'Anchor Insurance',
                'Anchor Logistics',
                'Anchor SmartCity',
                'Anchor Social',
                'Anchor AgroShield',
                'Anchor BioAgri',
                'Anchor Water Utilities',
                'Anchor Blackflight',
                'Anchor Sovereign Surveillance',
                'Anchor HumanKind Intelligence',
                'Anchor Semiconductor Intelligence',
                'Anchor Quantum',
              ].map((name) => (
                <div
                  key={name}
                  className="bg-slate-900/30 border border-white/10 rounded px-3 py-1.5"
                >
                  <span className="text-xs text-purple-300">{name}&trade;</span>
                </div>
              ))}
            </div>

            <h3 className="text-xl text-pink-400 mt-6 mb-3">2.3 Product &amp; Feature Marks</h3>
            <div className="grid gap-2 mt-3">
              {[
                'Anchor Safe Search',
                'Anchor Intelligence',
                'Federation Operations Board',
                'Sovereign Live Map',
                'Auto Report Dispatch',
                'SYMPHONIC Sector Widget',
                'DREADCORE Manifesto',
                'Anchor OS Institutional Edition',
                'Anchor National Telemetry Layer',
                'Anchor Deception Technology',
                'Anchor Digital Twin Security',
                'Anchor Quantum Cryptography',
                'Anchor Autonomous SOC',
                'Anchor Autonomous Red Team',
                'Anchor Breach Simulator',
                'Anchor Predictive CVE Intelligence',
                'Anchor AI Auto-Fix',
              ].map((name) => (
                <div
                  key={name}
                  className="bg-slate-900/30 border border-white/10 rounded px-3 py-1.5"
                >
                  <span className="text-xs text-purple-300">{name}&trade;</span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              3. Patents &amp; Patent-Pending Technologies
            </h2>
            <p>
              Anchor AI Guard has developed and deployed Sovereign Capabilities technologies. The following
              innovations are subject to patent protection (filed or pending) in relevant
              jurisdictions:
            </p>

            <h3 className="text-xl text-pink-400 mt-6 mb-3">
              3.1 Autonomous AI Security Innovations
            </h3>
            <ul className="list-disc list-inside space-y-2 text-sm mt-2">
              <li>
                <strong>Predictive CVE Intelligence:</strong> AI system that predicts
                vulnerabilities before public disclosure using pattern analysis across software
                ecosystems. Patent pending.
              </li>
              <li>
                <strong>AI Auto-Fix Engine:</strong> Autonomous code remediation system that
                generates and applies security patches without human intervention. Patent pending.
              </li>
              <li>
                <strong>Self-Evolving Threat Detection:</strong> AI engines that autonomously evolve
                their detection rules based on emerging threat patterns across 42 sector domains.
                Patent pending.
              </li>
              <li>
                <strong>Cross-Sector SYMPHONIC Intelligence:</strong> Method and system for
                correlating cybersecurity threats across multiple independent industry domains in
                real-time. Patent pending.
              </li>
              <li>
                <strong>Autonomous Business Development Engine:</strong> AI system that autonomously
                identifies, qualifies, and engages potential business opportunities across 42
                sectors. Patent pending.
              </li>
              <li>
                <strong>Kinetic-SI Smart Effector Intelligence:</strong> AI-powered weapons-grade
                licensing platform with per-effector royalty model, 3-layer ethical firewall, and
                Dud Mode failsafe. Patent pending.
              </li>
              <li>
                <strong>ASCA/DIDG Sovereign Defence Procurement Gateway:</strong> Automated
                procurement alignment system for Australian Signals Directorate and Defence Industry
                Security Programme compliance. Patent pending.
              </li>
            </ul>

            <h3 className="text-xl text-pink-400 mt-6 mb-3">
              3.2 Sovereign Architecture Innovations
            </h3>
            <ul className="list-disc list-inside space-y-2 text-sm mt-2">
              <li>
                <strong>Anchor OS Multi-Layer Architecture:</strong> Four-layer sovereign operating
                system (System Layer, Orchestration Layer, Intelligence Layer, Interface Layer) for
                institutional cybersecurity deployment. Patent pending.
              </li>
              <li>
                <strong>Confirmation Spine:</strong> Cross-sector verification and data integrity
                system ensuring provenance across all 42 sector operations. Patent pending.
              </li>
              <li>
                <strong>Sovereign Crypto Module:</strong> Encryption-at-rest system with sovereign
                key management for multi-sector data protection. Patent pending.
              </li>
              <li>
                <strong>9-Layer Sovereign Background System:</strong> Visual rendering system with 7
                neon rgba layers, hardlocked sector logo, and dark overlay for sovereign dashboard
                presentation. Patent pending.
              </li>
              <li>
                <strong>Federation Operations Board:</strong> Unified command-and-control interface
                for simultaneous oversight of 42 autonomous sector operations without breaching
                individual sector data sovereignty. Patent pending.
              </li>
            </ul>

            <h3 className="text-xl text-pink-400 mt-6 mb-3">3.3 Multi-Modal AI Innovations</h3>
            <ul className="list-disc list-inside space-y-2 text-sm mt-2">
              <li>
                <strong>TAZZIEDEVIL2.0 Multi-Modal Engine:</strong> Integrated AI system combining
                text, image, video, audio, and code generation with 42-sector domain intelligence.
                Patent pending.
              </li>
              <li>
                <strong>Sovereign Safe Search:</strong> Privacy-preserving search engine with zero
                tracking, zero ads, and sovereign data handling. Patent pending.
              </li>
              <li>
                <strong>Real-Time Satellite Imaging Analysis:</strong> AI system for real-time
                analysis of satellite imagery integrated with sector-specific threat intelligence.
                Patent pending.
              </li>
              <li>
                <strong>Gunshot Detection AI:</strong> Audio analysis system for detecting gunshots
                and classifying weapon types with emergency dispatch integration. Patent pending.
              </li>
              <li>
                <strong>CCTV Analysis Intelligence:</strong> Real-time video feed analysis with
                facial recognition, anomaly detection, and cross-sector threat correlation. Patent
                pending.
              </li>
            </ul>

            <h3 className="text-xl text-pink-400 mt-6 mb-3">
              3.4 Cybersecurity Operations Innovations
            </h3>
            <ul className="list-disc list-inside space-y-2 text-sm mt-2">
              <li>
                <strong>Autonomous SOC (Security Operations Centre):</strong> Fully autonomous
                security operations requiring zero human analysts for Tier 1-3 incident response.
                Patent pending.
              </li>
              <li>
                <strong>Autonomous Red Team:</strong> AI-driven continuous penetration testing
                system that evolves attack methodologies based on defensive responses. Patent
                pending.
              </li>
              <li>
                <strong>Digital Twin Security:</strong> System for creating and attacking digital
                replicas of production environments for proactive vulnerability discovery. Patent
                pending.
              </li>
              <li>
                <strong>Breach Simulator:</strong> Controlled breach simulation environment with
                automated impact assessment across 42 sector domains. Patent pending.
              </li>
              <li>
                <strong>Deception Technology Platform:</strong> National-scale honeypot and decoy
                system for threat intelligence gathering with cross-sector intelligence sharing.
                Patent pending.
              </li>
              <li>
                <strong>Attack Path Visualisation:</strong> Graph-based system for visualising and
                predicting adversary movement paths across complex multi-sector environments. Patent
                pending.
              </li>
              <li>
                <strong>Supply Chain AI:</strong> Autonomous supply chain security analysis with
                SBOM generation, dependency risk scoring, and auto-remediation. Patent pending.
              </li>
              <li>
                <strong>National Telemetry Layer:</strong> Sovereign-grade national cybersecurity
                monitoring infrastructure for government deployment. Patent pending.
              </li>
            </ul>

            <h3 className="text-xl text-pink-400 mt-6 mb-3">
              3.5 Compliance &amp; Governance Innovations
            </h3>
            <ul className="list-disc list-inside space-y-2 text-sm mt-2">
              <li>
                <strong>Autonomous Compliance Engine:</strong> AI system that continuously monitors,
                maps, and enforces regulatory compliance across 27+ international frameworks
                simultaneously. Patent pending.
              </li>
              <li>
                <strong>Mandatory Abuse Reporting System:</strong> Sub-5-second automated dispatch
                to 6 authorities for safeguarding incidents in medical and education sectors. Patent
                pending.
              </li>
              <li>
                <strong>Sovereign Data Residency Enforcement:</strong> Automated system ensuring
                data sovereignty compliance based on sector classification and jurisdictional
                requirements. Patent pending.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              4. Anchor OS &mdash; Sovereign Operating System
            </h2>
            <p>
              Anchor OS is the proprietary sovereign operating system that powers the Anchor AI
              Guard platform. It operates as a four-layer architecture:
            </p>
            <div className="space-y-3 mt-4">
              <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                <h4 className="font-semibold text-cyan-400">Layer 1: System Layer</h4>
                <p className="text-sm mt-1">
                  Core infrastructure management, service orchestration, health monitoring, security
                  middleware, prototype pollution prevention, IP blocking, rate limiting, and Helmet
                  CSP/HSTS enforcement.
                </p>
              </div>
              <div className="bg-slate-900/50 border border-purple-500/20 rounded-lg p-4">
                <h4 className="font-semibold text-purple-400">Layer 2: Orchestration Layer</h4>
                <p className="text-sm mt-1">
                  177 autonomous AI Evolution Engines (one per sector plus cross-sector SYMPHONIC
                  Engine, medical research engine, and BD engine), Confirmation Spine for
                  cross-sector verification, WebSocket real-time communication, and background
                  engine lifecycle management.
                </p>
              </div>
              <div className="bg-slate-900/50 border border-pink-500/20 rounded-lg p-4">
                <h4 className="font-semibold text-pink-400">Layer 3: Intelligence Layer</h4>
                <p className="text-sm mt-1">
                  DREADCORE SYMPHONIC Intelligence, TAZZIEDEVIL2.0 multi-modal AI, The Cortex
                  intelligence core, predictive CVE intelligence, AI auto-fix, autonomous compliance
                  engine, and cross-sector threat correlation.
                </p>
              </div>
              <div className="bg-slate-900/50 border border-emerald-500/20 rounded-lg p-4">
                <h4 className="font-semibold text-emerald-400">Layer 4: Interface Layer</h4>
                <p className="text-sm mt-1">
                  42 sovereign sector dashboards, Federation Operations Board, Sovereign Live Map,
                  TAZZIEDEVIL2.0 chatbot interface, anchorctl CLI, anchorsh interactive shell, and
                  400+ frontend components.
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm">
              Anchor OS is classified as proprietary software. Its architecture, implementation, and
              operational methodologies constitute trade secrets of Anchor AI Guard. Unauthorised
              reproduction, reverse engineering, decompilation, or disassembly of any component of
              Anchor OS is strictly prohibited.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              5. DREADCORE SI &amp; TAZZIEDEVIL2.0
            </h2>
            <p>
              DREADCORE SI (SYMPHONIC Intelligence) and TAZZIEDEVIL2.0 are the flagship AI products
              of Anchor AI Guard. They are the heroes of the business — the products that drive
              adoption, engagement, and sovereign AI capability across all 42 sector operations.
            </p>
            <div className="bg-slate-900/50 border border-pink-500/20 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-pink-400">
                DREADCORE SI — Autonomous Intelligence Engine
              </h4>
              <p className="text-sm mt-1">
                The self-evolving, self-correcting, future-operating AI engine that powers
                autonomous security operations across 42 sectors. DREADCORE SI includes proprietary
                algorithms for cross-sector threat correlation, predictive intelligence, autonomous
                evolution, and real-time decision-making.
              </p>
            </div>
            <div className="bg-slate-900/50 border border-emerald-500/20 rounded-lg p-4 mt-3">
              <h4 className="font-semibold text-emerald-400">
                TAZZIEDEVIL2.0 — Sovereign Multi-Modal AI
              </h4>
              <p className="text-sm mt-1">
                The sovereign AI chatbot exceeding frontier models. TAZZIEDEVIL2.0 provides text
                generation, image generation, video generation, audio generation, code generation,
                real-time data access, 42-sector intelligence, and safe search — all without
                tracking, surveillance, or data exploitation.
              </p>
            </div>
            <p className="mt-4 text-sm">
              All AI models, training data, weights, architectures, and operational algorithms of
              DREADCORE SI and TAZZIEDEVIL2.0 are the exclusive intellectual property of Anchor AI
              Guard. No licence, right, or interest in these AI systems is granted except as
              expressly provided in the applicable subscription agreement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">6. Copyright Notice</h2>
            <ul className="list-disc list-inside space-y-2 text-sm mt-2">
              <li>&copy; 2024-2026 Anchor AI Guard. All rights reserved.</li>
              <li>
                All source code, object code, compiled binaries, and associated documentation are
                protected by copyright law.
              </li>
              <li>
                All marketing materials, website content, landing pages, product descriptions, and
                promotional materials are protected by copyright law.
              </li>
              <li>
                All visual designs, UI/UX patterns, logos, icons, the 9-layer sovereign background
                system, and DREADCORE visual identity are protected by copyright and design rights.
              </li>
              <li>
                The Anchor AI Guard platform documentation, including all technical documentation,
                API documentation, and user guides, is protected by copyright law.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">7. Trade Secrets</h2>
            <p>
              The following constitute trade secrets of Anchor AI Guard and are subject to the
              strictest confidentiality protections:
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm mt-2">
              <li>AI model architectures, training methodologies, and neural network topologies</li>
              <li>Cross-sector intelligence correlation algorithms</li>
              <li>Autonomous evolution engine decision-making logic</li>
              <li>Predictive CVE intelligence pattern recognition systems</li>
              <li>Sovereign crypto key management and encryption schemes</li>
              <li>Confirmation Spine cross-sector verification protocols</li>
              <li>Autonomous business development engine targeting algorithms</li>
              <li>
                All internal platform metrics, performance benchmarks, and operational telemetry
              </li>
            </ul>
            <p className="mt-4 text-sm">
              Any unauthorised disclosure, use, or exploitation of Anchor AI Guard trade secrets
              will be pursued to the fullest extent of the law, including under the{' '}
              <strong>Trade Secrets Act (various jurisdictions)</strong>,
              <strong>Corporations Act 2001 (Cth)</strong>, and applicable international trade
              secret protections.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              8. Open Source Acknowledgements
            </h2>
            <p>
              The Anchor AI Guard platform incorporates certain open-source software components
              under their respective licences (MIT, Apache 2.0, BSD, ISC). These components are used
              as dependencies and do not affect the proprietary status of Anchor AI Guard&apos;s own
              intellectual property. A full list of open-source dependencies and their licences is
              available upon request.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">
              9. Enforcement &amp; Infringement
            </h2>
            <p>
              Anchor AI Guard actively monitors for and enforces against intellectual property
              infringement. If you become aware of any infringement, please report it to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm mt-2">
              <li>
                Email: <strong>legal@anchoraiguard.com</strong>
              </li>
              <li>
                Include: description of the IP being infringed, evidence of infringement, and your
                contact details
              </li>
              <li>Response time: Within 5 business days</li>
            </ul>
            <p className="mt-4">
              Anchor AI Guard reserves the right to take any and all legal action, including seeking
              injunctive relief, damages, and costs, against any party that infringes upon its
              intellectual property rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-cyan-400 mt-8 mb-4">10. Governing Law</h2>
            <p>
              This Intellectual Property Notice is governed by the laws of the Commonwealth of
              Australia and the State of New South Wales. Any disputes arising from or related to
              the intellectual property described herein shall be subject to the exclusive
              jurisdiction of the courts of New South Wales, Australia.
            </p>
            <p className="mt-4 text-sm">
              International intellectual property protections are asserted under the{' '}
              <strong>Berne Convention</strong>,<strong>Paris Convention</strong>,{' '}
              <strong>Patent Cooperation Treaty (PCT)</strong>, <strong>Madrid Protocol</strong>
              (for trade marks), and <strong>TRIPS Agreement</strong> (WTO).
            </p>
          </section>

          <section className="bg-linear-to-r from-cyan-500/10 to-pink-500/10 border border-cyan-500/30 rounded-xl p-6 mt-8">
            <h2 className="text-xl font-semibold text-white mb-2">IP &amp; Legal Inquiries</h2>
            <p className="mb-4">
              For licensing enquiries, trade mark usage requests, patent information, or IP
              concerns:
            </p>
            <a
              href="mailto:legal@anchoraiguard.com"
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Contact Legal Team
            </a>
          </section>

          <p className="text-sm text-gray-500 mt-8 border-t border-gray-700 pt-4">
            MR TODD ANTHONY MATHEW GOODWIN TA ANCHOR AI GUARD · ABN pending registration · Sydney,
            Australia · legal@anchoraiguard.com
          </p>
        </div>
      </div>
    </div>
  );
};
